import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import Node from './Node';
import Line from './Line';
import { ColorPickerWidget } from './ColorPickerWidget';
import { COLORS } from '../../config/colors';
import {
  DEFAULT_THEME_ID,
  getCanonicalPaletteColor,
  getMapTheme,
  getThemeCanvasBackground,
  getThemeCanvasForeground,
  getThemeCanvasSolidColor,
} from '../../config/themes';
import EmojiPickerWidget from './EmojiPickerWidget';
import ReparentDropIndicator from './ReparentDropIndicator';
import { useNodeManagement } from '../../hooks/mindmap/useNodeManagement';
import { useMapNavigation } from '../../hooks/mindmap/useMapNavigation';
import { useKeyboardShortcuts } from '../../hooks/mindmap/useKeyboardShortcuts';
import { useUndoRedo } from '../../hooks/mindmap/useUndoRedo';
import { useCollisionDetection } from '../../hooks/mindmap/useCollisionDetection';
import { usePersistence } from '../../hooks/mindmap/usePersistence';
import { useVisibility } from '../../hooks/mindmap/useVisibility';
import { useYjsMap } from '../../hooks/mindmap/useYjsMap';
import { MindMapState } from '../../models/MindMapState';
import { EXPORT_FORMATS, FileExporter, FileImporter } from '../../utils/mindmap/serialization';
import Menu from './Menu';
import FocusNavigation from './FocusNavigation';
import ShareDialog from './ShareDialog';
import NodeDiscussionPanel from './NodeDiscussionPanel';
import { getHealth, fetchUrlPreview, createMindMap, updateMindMapTitle, getAuthToken, uploadMindMapThumbnail } from '../../utils/mindmap/apiClient';
import NodeContextMenu from './NodeContextMenu';
import { useNodeDiscussions } from '../../hooks/mindmap/useNodeDiscussions';
import {
  isNamedDiscussionCollaborator,
} from '../../utils/mindmap/nodeDiscussionEvents';
import { clampViewportOverlay, clientToViewport, screenToWorld, worldToViewport } from '../../utils/mindmap/viewport';
import { readNodeLayoutSize } from '../../utils/mindmap/nodeMeasurements';
import {
  createDragPositionPreview,
  dragPreviewMatchesNodes,
  getRenderedNodePosition,
} from '../../utils/mindmap/dragPreview';
import { createLinkedMapPortal } from '../../utils/mindmap/linkedMapWorkflow';
import {
  buildLinkedMapTitlePatch,
  publishMapTitleUpdate,
  subscribeToMapTitleUpdates,
} from '../../utils/mindmap/mapTitleSync';
import {
  deriveVisibleNodeIds,
  getAncestorNodeIds,
  getBranchNodeIds,
  getNodePath,
} from '../../utils/mindmap/branchVisibility';
import {
  createFocusHistory,
  currentFocusId,
  pushFocusHistory,
  pruneFocusHistory,
  stepFocusHistory,
} from '../../utils/mindmap/focusHistory';
import {
  buildMoveSubtreeCommand,
  buildCreateChildCommand,
  buildReparentSubtreeCommand,
  buildCreateSiblingCommand,
  buildCreateTaskChildCommand,
  buildCreateLinkedMapCommand,
  buildDeleteSubtreeCommand,
  resolveMoveCommandCollisions,
  topLeftRectsOverlap,
} from '../../utils/mindmap/mapCommands';
import { searchNodes } from '../../utils/mindmap/nodeSearch';
import { routeEdges } from '../../utils/mindmap/edgeRouting';

// Minimal HTML entities decoder for titles coming from URL metadata.
// Covers the most common entities we see in practice.
const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

const buildMindMapShareUrl = (mindmapId) => {
  if (typeof window === 'undefined') return `?map=${encodeURIComponent(mindmapId)}`;
  const envBase = typeof process !== 'undefined' && process.env.REACT_APP_FRONTEND_BASE_URL
    ? process.env.REACT_APP_FRONTEND_BASE_URL
    : null;
  const baseUrl = (envBase || `${window.location.origin}${window.location.pathname}`).replace(/\/$/, '');
  return `${baseUrl}?map=${encodeURIComponent(mindmapId)}`;
};

const DEFAULT_CANVAS_SPAN = 2000;
const NODE_WIDTH_GUESS = 260;
const NODE_HEIGHT_GUESS = 110;
const CANVAS_PADDING = 600;
const COLLISION_SLIDE_MS = 180;
const NODE_DOUBLE_CLICK_GUARD_MS = 500;
const NODE_DOUBLE_CLICK_GUARD_DISTANCE = 24;
const MAP_SEARCH_INPUT_SELECTOR = '[data-map-search-input="true"]';

const NodesContainer = ({
  onBaseNodeChange,
  currentUserId,
  currentUserName = null,
  remoteMapData,
  onRegisterSaveHandler,
  onRegisterMapFileActions,
  onMapInitialized,
  onDistractionFreeChange,
}) => {
  const containerRef = useRef(null);
  const captureRef = useRef(null);
  const nodeRefs = useRef({});
  const measuredNodeSizesRef = useRef(new Map());
  const [showPicker, setShowPicker] = useState(false);
  const [colorPosOverride, setColorPosOverride] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPos, setEmojiPos] = useState({ x: 0, y: 0 });
  const [isDistractionFree, setIsDistractionFree] = useState(false);
  const [areFullscreenMenusVisible, setAreFullscreenMenusVisible] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('mindMapTheme');
      if (stored === 'light') return false;
      if (stored === 'dark') return true;
      return true;
    } catch (e) {
      console.warn('Failed to read stored theme, defaulting to dark:', e);
      return true;
    }
  });
  // Map theme for local (signed-out) maps; remote maps read the theme from
  // the shared Yjs meta instead so every collaborator sees the same look.
  const [localThemeId, setLocalThemeId] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME_ID;
    try {
      return window.localStorage.getItem('mindMapMapTheme') || DEFAULT_THEME_ID;
    } catch {
      return DEFAULT_THEME_ID;
    }
  });
  const [clickTimeout, setClickTimeout] = useState(null);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [dragPositionPreview, setDragPositionPreview] = useState(() => new Map());
  const [layoutTransitionNodeIds, setLayoutTransitionNodeIds] = useState(() => new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const didCallMapInitialized = useRef(false);
    // Call onMapInitialized after first full render when map is ready
    useEffect(() => {
      if (isInitialized && onMapInitialized && !didCallMapInitialized.current) {
        didCallMapInitialized.current = true;
        onMapInitialized();
      }
    }, [isInitialized, onMapInitialized]);
  const lastSavedStateRef = useRef(null);
  const lastPersistedStateRef = useRef(null);
  const lastSelectedIdRef = useRef(null);
  const urlNodeHandledRef = useRef(false);
  const [focusRootId, setFocusRootId] = useState(null);
  const [collapsedNodeIds, setCollapsedNodeIds] = useState(() => new Set());
  const [temporarilyExpandedNodeIds, setTemporarilyExpandedNodeIds] = useState(() => new Set());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActiveIndex, setSearchActiveIndex] = useState(0);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [focusHistory, setFocusHistory] = useState(() => createFocusHistory());
  const hasLoadedRef = useRef(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, nodeId: null, x: 0, y: 0 });
  const contextMenuOpenedAtRef = useRef(0);
  const lastSaveRequestRef = useRef(0);
  const hasUnsavedChangesRef = useRef(false);
  const centeredInitialViewportKeyRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const rootPanDragRef = useRef(null);
  const dragGestureRef = useRef(null);
  const dragFrameRef = useRef({ rafId: null, pendingCommand: null });
  const resizeLayoutFrameRef = useRef(null);
  const layoutTransitionTimerRef = useRef(null);
  const reparentTargetRef = useRef(null);
  const linkedMapCreationRef = useRef(new Set());
  const latestGraphRef = useRef({ nodes: [], relationships: [] });
  const pendingAutoFocusNodeIdRef = useRef(null);
  const recentNodePointerDownRef = useRef(null);

  const focusMapSearchInput = useCallback((select = false) => {
    if (typeof document === 'undefined') return;
    const input = document.querySelector(MAP_SEARCH_INPUT_SELECTOR);
    if (!input) return;
    input.focus();
    if (select && typeof input.select === 'function') input.select();
  }, []);

  const openMapSearch = useCallback((options = {}) => {
    setIsSearchOpen(true);
    if (options.focus === false || typeof window === 'undefined') return;
    window.requestAnimationFrame(() => focusMapSearchInput(options.select !== false));
  }, [focusMapSearchInput]);

  const closeMapSearch = useCallback((options = {}) => {
    setIsSearchOpen(false);
    if (options.blur !== true || typeof document === 'undefined') return;
    const input = document.querySelector(MAP_SEARCH_INPUT_SELECTOR);
    if (input && document.activeElement === input) input.blur();
  }, []);

  const updateMapSearchQuery = useCallback((query) => {
    setSearchQuery(typeof query === 'string' ? query : '');
    setSearchActiveIndex(0);
    setIsSearchOpen(true);
  }, []);

  const accessRole = remoteMapData?.accessRole
    || (remoteMapData?.ownerUserId === currentUserId ? 'owner' : null);
  // Remote maps always use the collaboration document, including anonymous
  // public/link visitors. The existing local flow remains available only when
  // there is no authorized remote map.
  const isLoggedIn = Boolean(remoteMapData?.mindmapId && accessRole);
  const canWriteMap = !isLoggedIn || accessRole === 'owner' || accessRole === 'editor';
  const canManageMapMetadata = !isLoggedIn || accessRole === 'owner';

  const showNotification = useCallback((message, variant = 'info') => {
    if (!message) return;
    const id = Date.now() + Math.random();
    setNotifications((prev) => [...prev, { id, message, variant }]);

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  }, []);

  const exitDistractionFree = useCallback(() => {
    setIsDistractionFree(false);
    setAreFullscreenMenusVisible(false);
    if (typeof document === 'undefined') return;
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
    const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
    if (fullscreenElement && typeof exitFullscreen === 'function') {
      Promise.resolve(exitFullscreen.call(document)).catch(() => {
        // The app-level distraction-free view has still been exited.
      });
    }
  }, []);

  const enterDistractionFree = useCallback(() => {
    setIsDistractionFree(true);
    setAreFullscreenMenusVisible(false);
    if (typeof document === 'undefined') return;
    const fullscreenTarget = document.documentElement;
    const requestFullscreen = fullscreenTarget?.requestFullscreen
      || fullscreenTarget?.webkitRequestFullscreen;
    if (typeof requestFullscreen === 'function') {
      Promise.resolve(requestFullscreen.call(fullscreenTarget)).catch(() => {
        // Browsers may deny fullscreen; the distraction-free layout still works.
      });
    }
  }, []);

  useEffect(() => {
    if (typeof onDistractionFreeChange === 'function') {
      onDistractionFreeChange({
        active: isDistractionFree,
        menusVisible: areFullscreenMenusVisible,
      });
    }
  }, [
    areFullscreenMenusVisible,
    isDistractionFree,
    onDistractionFreeChange,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fullscreenElement) {
        setIsDistractionFree(false);
        setAreFullscreenMenusVisible(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!isDistractionFree || typeof window === 'undefined') return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') exitDistractionFree();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [exitDistractionFree, isDistractionFree]);

  useEffect(() => () => {
    if (typeof onDistractionFreeChange === 'function') {
      onDistractionFreeChange({ active: false, menusVisible: false });
    }
  }, [onDistractionFreeChange]);

  // On initial mount, ping the backend health endpoint so we can
  // verify browser → API connectivity and CORS configuration.
  useEffect(() => {
    let cancelled = false;

    getHealth()
      .then((data) => {
        if (!cancelled) {
          // For now, just log the result; we can surface this in the UI later.
          console.log('Backend health:', data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Backend health check failed:', err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  // Persist theme selection so dark/light mode is restored on reload
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('mindMapTheme', isDarkMode ? 'dark' : 'light');
    } catch (e) {
      console.error('Failed to save theme preference:', e);
    }
  }, [isDarkMode]);


    // Reparenting candidate shown while a dragged subtree overlaps a valid parent.
    const [reparentCandidate, setReparentCandidate] = useState({
      visible: false,
      draggedNode: null,
      targetNode: null,
      position: { x: 0, y: 0 }
    });
    const [draggedNodeId, setDraggedNodeId] = useState(null);
    const [highlightedNodeId, setHighlightedNodeId] = useState(null);

    // Initialize collision detection
    const { resolveCollisions } = useCollisionDetection({
      nodeRefs,
      minDistance: 20,
      animationDuration: 300
    });

    // Initialize undo/redo for local maps. Remote maps rely on Yjs history.
    const {
      saveVersion,
      undo: localUndo,
      redo: localRedo,
      canUndo: localCanUndo,
      canRedo: localCanRedo,
    } = useUndoRedo({ maxHistorySize: 20 });

    // Initialize node management
    const nodeManagement = useNodeManagement({
      containerRef,
      saveVersion,
      resolveCollisions,
      currentUserId,
      currentUserName,
    });

    const {
      nodes,
      relationships,
      selectedId,
      root,
      setSelectedId,
      setNodes,
      getNode,
      getSelectedNode,
      getParentNode,
      getChildren,
      insertNodeWithRelationship,
      applyMoveNodesCommand,
      applyReparentSubtreeCommand,
      applyDeleteSubtreeCommand,
      updateNodeText,
      updateNodePosition,
      setNodeColor,
      toggleTaskFlag,
      toggleTaskDone,
      clearAutoFocus,
    } = nodeManagement;

  const searchResults = useMemo(
    () => searchNodes(nodes, searchQuery),
    [nodes, searchQuery]
  );

  useEffect(() => {
    setSearchActiveIndex((current) => (
      Math.min(current, Math.max(searchResults.length - 1, 0))
    ));
  }, [searchResults.length]);

  const moveMapSearchSelection = useCallback((delta) => {
    if (!searchResults.length) return;
    setSearchActiveIndex((current) => (
      (current + delta + searchResults.length) % searchResults.length
    ));
  }, [searchResults.length]);

  latestGraphRef.current = { nodes, relationships };

  // Initialize persistence (used by both guest and logged-in flows)
  const { saveMindMap, loadMindMap, loadMindMapFromData } = usePersistence({
    nodeManagement,
    saveVersion
  });

  // Expose saveMindMap to parent (App) so it can force
  // a save on sign-out or other global events.
  useEffect(() => {
    if (typeof onRegisterSaveHandler === 'function') {
      onRegisterSaveHandler(saveMindMap);
    }
  }, [onRegisterSaveHandler, saveMindMap]);

  // --- Yjs / Hocuspocus integration for authorized remote maps ---

  const [mindmapIdForYjs, setMindmapIdForYjs] = useState(null);
  const prevMindmapIdRef = useRef(null);

  // Resolve the mindmap id for this view: prefer backend-provided
  // id, fall back to ?map= query param when present.
  useEffect(() => {
    let id = null;

    if (remoteMapData && remoteMapData.mindmapId) {
      id = remoteMapData.mindmapId;
    }

    if (!id && typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        const mapParam = url.searchParams.get('map');
        if (mapParam) id = mapParam;
      } catch {
        // ignore URL parse errors
      }
    }

    setMindmapIdForYjs(id || null);
  }, [remoteMapData]);

  // Viewport reset effect relocated below where setViewport is defined

  const [yInitialData, setYInitialData] = useState(null);

  // Provide initial snapshot only for the first load when we have
  // legacy JSON data from the backend; Yjs will seed from this
  // exactly once when the document is empty.
  useEffect(() => {
    if (!isLoggedIn) {
      setYInitialData(null);
      return;
    }

    if (remoteMapData && Array.isArray(remoteMapData.nodes)) {
      setYInitialData({
        nodes: remoteMapData.nodes,
        relationships: remoteMapData.relationships || [],
        root: remoteMapData.root || null,
        meta: {},
      });
    } else {
      setYInitialData(null);
    }
  }, [isLoggedIn, remoteMapData]);

  const authToken = isLoggedIn ? getAuthToken() : null;

  const {
    yState,
    actions: yActions,
    isSynced: yIsSynced,
    isLocallySynced: yIsLocallySynced,
    isNetworkSynced: yIsNetworkSynced,
    connectionStatus: yConnectionStatus,
    yProvider,
  } = useYjsMap({
    mindmapId: isLoggedIn && accessRole ? mindmapIdForYjs : null,
    initialData: isLoggedIn ? yInitialData : null,
    authToken,
    readOnly: !canWriteMap,
    currentUserId,
    currentUserName,
    selectedNodeId: selectedId,
  }); //yProvider,

  // Resolve the active map theme. Remote maps store it in the shared Yjs
  // meta (so it syncs live and persists with the map); local maps fall back
  // to the locally persisted choice. Unknown ids resolve to the default.
  const activeThemeId = isLoggedIn
    ? (yState?.meta?.theme || DEFAULT_THEME_ID)
    : localThemeId;
  const mapTheme = getMapTheme(activeThemeId);

  const setMapTheme = useCallback((themeId) => {
    // UI-level enforcement: only the map owner may change a shared map's
    // theme. (Server-side enforcement is a known follow-up.)
    if (!canManageMapMetadata) return false;
    if (isLoggedIn) {
      return !!(yActions?.setTheme && yActions.setTheme(themeId));
    }
    const nextThemeId = themeId || DEFAULT_THEME_ID;
    setLocalThemeId(nextThemeId);
    try {
      window.localStorage.setItem('mindMapMapTheme', nextThemeId);
    } catch {
      // The theme still applies for this session.
    }
    return true;
  }, [canManageMapMetadata, isLoggedIn, yActions]);

  const hasSeededYDocRef = useRef(false);
  const hasHydratedFromYjsRef = useRef(false);

  // Keep React node state as a projection of the Yjs document for
  // remote-map visitors. We deliberately do not restore selectedId from
  // Yjs – it remains a per-user UI concern.
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!yState) return;

    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.log('[YJS] yState snapshot:', yState);
    }

    let projected = yState;

    const hasNodes = Array.isArray(yState.nodes) && yState.nodes.length > 0;
    const hasRoot = !!yState.root;

    // For brand-new writable maps with an empty Yjs document,
    // create a default "New Node" root. If we can write to Yjs,
    // seed the document first and wait for the next yState update.
    if (!hasNodes && !hasRoot && !hasSeededYDocRef.current) {
      // If the provider hasn't completed its initial sync yet,
      // we can't safely decide that the document is truly empty.
      // In this case, skip seeding for now but still project the
      // current (possibly empty) state so the UI can render while
      // we wait for remote content.
      if (!yIsSynced) {
        // Fall through to projection without seeding.
      } else {
      const id = crypto.randomUUID();

      let position = { x: 300, y: 300 };
      try {
        if (containerRef.current && containerRef.current.getBoundingClientRect) {
          const rect = containerRef.current.getBoundingClientRect();
          position = {
            x: rect.width / 2 - 100,
            y: rect.height / 2 - 30,
          };
        }
      } catch {
        // fall back to default position
      }

      const color = COLORS[0]?.gradient || null;

      const newNode = {
        id,
        text: 'New Node',
        position,
        color,
        isRoot: true,
        collapsedChildren: null,
        embeddedMindmapId: null,
        linkUrl: null,
        linkMeta: null,
        isTask: false,
        taskDone: false,
        emoji: null,
        isEmbeddedMindmapLink: false,
      };

      if (yActions && yActions.addNode && yActions.setRoot) {
        hasSeededYDocRef.current = true;
        yActions.addNode(newNode);
        yActions.setRoot(id);
        // Wait for Yjs to apply the change and update yState
        return;
      }

      // Fallback: if we can't write to Yjs for some reason,
      // at least project a local default node so the UI isn't blank.
      projected = {
        ...yState,
        nodes: [newNode],
        relationships: [],
        root: id,
      };
      }
    }

    let data = {
      nodes: projected.nodes || [],
      relationships: projected.relationships || [],
      root: projected.root || null,
    };

    // On first hydration from Yjs, initialize selectedId to the root
    // so keyboard shortcuts and "Add child" work without requiring
    // a manual click. After that, selectedId stays purely local.
    const isFirstHydration = !hasHydratedFromYjsRef.current;
    if (isFirstHydration) {
      const rootId = data.root || (data.nodes.length > 0 ? data.nodes[0].id : null);
      data.selectedId = rootId;
    }

    const shouldPreserveSelection = !isFirstHydration;

    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.log('[YJS] Projected data into React:', data, 'preserveSelectedId:', shouldPreserveSelection);
    }

    loadMindMapFromData(data, { preserveSelectedId: shouldPreserveSelection });
    if (isFirstHydration) {
      hasHydratedFromYjsRef.current = true;
    }
    hasLoadedRef.current = true;
  }, [isLoggedIn, yState, yIsSynced, yActions, loadMindMapFromData]);

  // When a user signs in, tag any anonymous nodes with their userId
  useEffect(() => {
    if (!currentUserId) return;
    setNodes(prev => prev.map(node => (
      node.userId ? node : { ...node, userId: currentUserId }
    )));
  }, [currentUserId, setNodes]);

  const contentBounds = useMemo(() => {
    void layoutVersion; // ensures layoutVersion participates without altering math
    if (!Array.isArray(nodes) || nodes.length === 0) {
      const span = DEFAULT_CANVAS_SPAN / 2;
      return {
        minX: -span,
        maxX: span,
        minY: -span,
        maxY: span,
      };
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    nodes.forEach((node) => {
      if (!node || !node.position) return;
      const left = node.position.x;
      const right = node.position.x + NODE_WIDTH_GUESS;
      const top = node.position.y;
      const bottom = node.position.y + NODE_HEIGHT_GUESS;

      minX = Math.min(minX, left);
      maxX = Math.max(maxX, right);
      minY = Math.min(minY, top);
      maxY = Math.max(maxY, bottom);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
      const span = DEFAULT_CANVAS_SPAN / 2;
      return {
        minX: -span,
        maxX: span,
        minY: -span,
        maxY: span,
      };
    }

    return {
      minX: minX - CANVAS_PADDING,
      maxX: maxX + CANVAS_PADDING,
      minY: minY - CANVAS_PADDING,
      maxY: maxY + CANVAS_PADDING,
    };
  }, [nodes, layoutVersion]);

  const stableMindmapId = mindmapIdForYjs || (remoteMapData && remoteMapData.mindmapId) || null;
  const viewportStorageKey = stableMindmapId || (!isLoggedIn ? 'guest-map' : null);

  const canvasWidth = Math.max(
    DEFAULT_CANVAS_SPAN,
    contentBounds.maxX - contentBounds.minX
  );
  const canvasHeight = Math.max(
    DEFAULT_CANVAS_SPAN,
    contentBounds.maxY - contentBounds.minY
  );

  // Initialize map navigation. For logged-in users, panning/zooming
  // update positions through Yjs so the projection stays consistent
  // across tabs. Guest users use the local state helper.
  const moveNodeForNavigation = useCallback((nodeId, position, options) => {
    if (isLoggedIn && yActions && yActions.updateNode) {
      yActions.updateNode(nodeId, { position, lastEditedBy: currentUserId });
      return;
    }
    updateNodePosition(nodeId, position, options);
  }, [isLoggedIn, yActions, updateNodePosition, currentUserId]);

  const mapNavigation = useMapNavigation({
    containerRef,
    root,
    nodes,
    moveNode: moveNodeForNavigation,
    contentBounds,
    viewportStorageKey,
  });

  const {
    scale,
    offset,
    didRestoreViewport,
    centerNode,
    focusNodeIfOutOfView,
    zoomIn,
    zoomOut,
    resetZoom,
    fitToBounds,
    panBy,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp: navHandleMouseUp,
    handlePointerDown: navHandlePointerDown,
    handlePointerMove: navHandlePointerMove,
    handlePointerUp: navHandlePointerUp,
    handlePointerCancel: navHandlePointerCancel,
    getViewport,
    setViewport
  } = mapNavigation;

  // Center a node using its visual center rather than top-left
  const centerNodePrecise = useCallback((nodeId, options = {}) => {
    const node = nodes.find(n => n.id === nodeId);
    const containerRect = containerRef.current?.getBoundingClientRect();
    const nodeRef = nodeRefs.current[nodeId];
    const positionOverride = options && options.positionOverride ? options.positionOverride : null;

    if (!containerRect) {
      centerNode(nodeId);
      return;
    }

    let width = 200;
    let height = 60;
    const measuredSize = readNodeLayoutSize(nodeRef, scale);
    if (measuredSize) {
      width = measuredSize.width;
      height = measuredSize.height;
    }

    const targetPosition = positionOverride || (node ? node.position : null);
    if (!targetPosition) {
      centerNode(nodeId);
      return;
    }

    const viewportCenterX = containerRect.width / 2;
    const viewportCenterY = containerRect.height / 2;

    const nodeCenterX = (targetPosition.x + width / 2) * scale;
    const nodeCenterY = (targetPosition.y + height / 2) * scale;

    const newOffsetX = viewportCenterX - nodeCenterX;
    const newOffsetY = viewportCenterY - nodeCenterY;

    if (typeof setViewport === 'function') {
      setViewport({ offset: { x: newOffsetX, y: newOffsetY } });
    }
  }, [nodes, containerRef, scale, centerNode, setViewport]);

  // Initialize visibility management
  const { getVisibleNodeIds } = useVisibility({
    nodes,
    relationships,
    focusRootId,
    collapsedNodeIds,
    temporarilyExpandedNodeIds,
  });

  const visibleNodeIds = useMemo(() => getVisibleNodeIds(), [getVisibleNodeIds]);
  const visibleDiscussionNodeIds = useMemo(
    () => [...visibleNodeIds],
    [visibleNodeIds]
  );
  const canUseNodeDiscussions = isNamedDiscussionCollaborator({
    currentUserId,
    isLoggedIn,
    accessRole,
    accessSource: remoteMapData?.accessSource,
  });
  const nodeDiscussions = useNodeDiscussions({
    mindmapId: mindmapIdForYjs,
    visibleNodeIds: visibleDiscussionNodeIds,
    enabled: canUseNodeDiscussions,
    provider: yProvider,
  });

  useEffect(() => {
    if (!viewportStorageKey || typeof window === 'undefined') {
      setCollapsedNodeIds(new Set());
      return;
    }
    try {
      const raw = window.localStorage.getItem(`mindMapCollapsed:${viewportStorageKey}`);
      const storedIds = raw ? JSON.parse(raw) : [];
      setCollapsedNodeIds(new Set(Array.isArray(storedIds) ? storedIds : []));
    } catch (error) {
      console.warn('Failed to restore collapsed branches:', error);
      setCollapsedNodeIds(new Set());
    }
  }, [viewportStorageKey]);

  useEffect(() => {
    if (!viewportStorageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        `mindMapCollapsed:${viewportStorageKey}`,
        JSON.stringify([...collapsedNodeIds])
      );
    } catch (error) {
      console.warn('Failed to persist collapsed branches:', error);
    }
  }, [collapsedNodeIds, viewportStorageKey]);

  useEffect(() => {
    const validIds = new Set(nodes.map((node) => node.id));
    setCollapsedNodeIds((previous) => {
      const next = new Set([...previous].filter((nodeId) => (
        validIds.has(nodeId) && getBranchNodeIds(nodeId, relationships).size > 1
      )));
      if (next.size === previous.size && [...next].every((nodeId) => previous.has(nodeId))) {
        return previous;
      }
      return next;
    });
  }, [nodes, relationships]);

  const collapseBranch = useCallback((nodeId) => {
    if (getBranchNodeIds(nodeId, relationships).size <= 1) return false;
    setTemporarilyExpandedNodeIds((previous) => {
      if (!previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.delete(nodeId);
      return next;
    });
    setCollapsedNodeIds((previous) => {
      if (previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.add(nodeId);
      return next;
    });
    return true;
  }, [relationships]);

  const expandBranch = useCallback((nodeId) => {
    setTemporarilyExpandedNodeIds((previous) => {
      if (!previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.delete(nodeId);
      return next;
    });
    setCollapsedNodeIds((previous) => {
      if (!previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.delete(nodeId);
      return next;
    });
  }, []);

  const revealNode = useCallback((nodeId) => {
    const ancestors = getAncestorNodeIds(nodeId, relationships);
    setCollapsedNodeIds((previous) => {
      const next = new Set([...previous].filter((collapsedId) => !ancestors.has(collapsedId)));
      return next.size === previous.size ? previous : next;
    });
  }, [relationships]);

  const getBoundsForNodeIds = useCallback((nodeIds) => {
    const requestedIds = nodeIds instanceof Set ? nodeIds : new Set(nodeIds || []);
    const requestedNodes = nodes.filter((node) => requestedIds.has(node.id) && node.position);
    if (!requestedNodes.length) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    requestedNodes.forEach((node) => {
      const ref = nodeRefs.current[node.id];
      const measuredSize = readNodeLayoutSize(ref, scale);
      const width = measuredSize?.width || node.layoutSize?.width || NODE_WIDTH_GUESS;
      const height = measuredSize?.height || node.layoutSize?.height || NODE_HEIGHT_GUESS;
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + width);
      maxY = Math.max(maxY, node.position.y + height);
    });
    return { minX, minY, maxX, maxY };
  }, [nodes, scale]);

  const fitNodeIds = useCallback((nodeIds, options = {}) => {
    const bounds = getBoundsForNodeIds(nodeIds);
    if (!bounds) return null;
    return fitToBounds(bounds, {
      padding: options.padding ?? 96,
      maxScale: options.maxScale ?? 1.25,
    });
  }, [fitToBounds, getBoundsForNodeIds]);

  const handleFitVisible = useCallback(() => {
    fitNodeIds(visibleNodeIds);
  }, [fitNodeIds, visibleNodeIds]);

  const handleFitSelection = useCallback(() => {
    if (!selectedId) {
      handleFitVisible();
      return;
    }
    const branchIds = getBranchNodeIds(selectedId, relationships);
    const visibleBranchIds = new Set([...branchIds].filter((nodeId) => visibleNodeIds.has(nodeId)));
    fitNodeIds(visibleBranchIds.size ? visibleBranchIds : new Set([selectedId]));
  }, [fitNodeIds, handleFitVisible, relationships, selectedId, visibleNodeIds]);

  const updateFocusView = useCallback((requestedNodeId, options = {}) => {
    const nextNode = requestedNodeId ? getNode(requestedNodeId) : null;
    const nextFocusRootId = nextNode ? nextNode.id : null;
    const shouldRecord = options.record !== false;
    const shouldFit = options.fit !== false;

    setFocusRootId(nextFocusRootId);
    if (nextFocusRootId) {
      setSelectedId(nextFocusRootId);
    } else if (root) {
      setSelectedId(root);
    }
    if (shouldRecord) {
      setFocusHistory((previous) => pushFocusHistory(previous, nextFocusRootId));
    }
    if (onBaseNodeChange) {
      onBaseNodeChange(nextNode?.text || null);
    }

    if (typeof window !== 'undefined') {
      const current = new URL(window.location.href);
      if (nextFocusRootId) current.searchParams.set('node', nextFocusRootId);
      else current.searchParams.delete('node');
      window.history.replaceState({}, '', current.toString());
    }

    if (shouldFit && typeof window !== 'undefined') {
      const effectiveCollapsedIds = new Set(collapsedNodeIds);
      if (nextFocusRootId) effectiveCollapsedIds.delete(nextFocusRootId);
      const nextVisibleIds = deriveVisibleNodeIds({
        nodes,
        relationships,
        focusRootId: nextFocusRootId,
        collapsedNodeIds: effectiveCollapsedIds,
        temporarilyExpandedNodeIds,
      });
      window.requestAnimationFrame(() => fitNodeIds(nextVisibleIds));
    }
  }, [
    collapsedNodeIds,
    fitNodeIds,
    getNode,
    nodes,
    onBaseNodeChange,
    relationships,
    root,
    setSelectedId,
    temporarilyExpandedNodeIds,
  ]);

  const focusPath = useMemo(() => {
    if (!focusRootId) return [];
    const nodesById = new Map(nodes.map((node) => [node.id, node]));
    return getNodePath(focusRootId, relationships)
      .map((nodeId) => nodesById.get(nodeId))
      .filter(Boolean);
  }, [focusRootId, nodes, relationships]);

  const navigateFocusHistory = useCallback((direction) => {
    const nextHistory = stepFocusHistory(focusHistory, direction);
    if (nextHistory === focusHistory) return;
    setFocusHistory(nextHistory);
    updateFocusView(currentFocusId(nextHistory), { record: false });
  }, [focusHistory, updateFocusView]);

  const handleSearchResult = useCallback((nodeId) => {
    const node = getNode(nodeId);
    if (!node) return;
    const ancestors = getAncestorNodeIds(nodeId, relationships);
    setTemporarilyExpandedNodeIds(new Set(
      [...ancestors].filter((ancestorId) => collapsedNodeIds.has(ancestorId))
    ));

    if (focusRootId && !getBranchNodeIds(focusRootId, relationships).has(nodeId)) {
      updateFocusView(null, { fit: false });
    }
    setSelectedId(nodeId);
    closeMapSearch({ blur: true });
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => centerNodePrecise(nodeId));
      });
    }
  }, [
    centerNodePrecise,
    collapsedNodeIds,
    closeMapSearch,
    focusRootId,
    getNode,
    relationships,
    setSelectedId,
    updateFocusView,
  ]);

  const selectActiveMapSearchResult = useCallback(() => {
    const result = searchResults[searchActiveIndex];
    if (result) handleSearchResult(result.node.id);
  }, [handleSearchResult, searchActiveIndex, searchResults]);

  // Center the initial view on the root node once, for new sessions
  // where the viewport is still at its default (scale 1, offset {0,0}).
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!nodes.length) return;
    if (didRestoreViewport) return;

    const rootId = root || (nodes[0] && nodes[0].id);
    if (!rootId) return;
    const viewportRootKey = `${viewportStorageKey || 'anonymous'}:${rootId}`;
    if (centeredInitialViewportKeyRef.current === viewportRootKey) return;

    centerNodePrecise(rootId);
    centeredInitialViewportKeyRef.current = viewportRootKey;
  }, [nodes, root, centerNodePrecise, didRestoreViewport, viewportStorageKey]);

  const getCurrentHistoryState = useCallback(() => ({
    nodes,
    relationships,
    root,
    selectedId,
    viewport: typeof getViewport === 'function' ? getViewport() : null,
  }), [nodes, relationships, root, selectedId, getViewport]);

  // Helper function to save current state to undo stack
  const saveCurrentState = useCallback(() => {
    // When logged in, structural state is owned by Yjs and we use
    // its UndoManager. Skip local snapshots to avoid conflicts.
    if (isLoggedIn) return;

    const currentState = getCurrentHistoryState();
    const currentStateStr = JSON.stringify(currentState);

    if (lastSavedStateRef.current !== currentStateStr) {
      console.log('Saving current state to undo stack');
      saveVersion(currentState);
      lastSavedStateRef.current = currentStateStr;
    }
  }, [getCurrentHistoryState, saveVersion, isLoggedIn]);

  // Stable callback so node size-change notifications don't create
  // a render loop by changing on every render.
  const handleNodeSizeChange = useCallback((nodeId, size) => {
    if (nodeId && size) measuredNodeSizesRef.current.set(nodeId, size);
    setLayoutVersion(v => v + 1);
  }, []);

  // When switching to a different mindmap, allow the initial centering effect
  // (or stored viewport) to run again for the new document.
  useEffect(() => {
    if (!mindmapIdForYjs) return;
    if (prevMindmapIdRef.current === mindmapIdForYjs) return;

    prevMindmapIdRef.current = mindmapIdForYjs;
    pendingAutoFocusNodeIdRef.current = null;
    centeredInitialViewportKeyRef.current = null;
    urlNodeHandledRef.current = false;
    setFocusRootId(null);
    setFocusHistory(createFocusHistory());
    setTemporarilyExpandedNodeIds(new Set());
    setIsSearchOpen(false);
  }, [mindmapIdForYjs]);

  // Helper to perform an immediate persistence of the current state.
  // Optionally accepts an override `{ nodeId, text }` so we can
  // persist the exact text the user finished typing for a given
  // node, even if React state updates haven't fully flushed yet.
  const persistNow = useCallback((override) => {
    if (!hasLoadedRef.current) return;
    if (!nodes.length) return;

    let snapshotNodes = nodes;
    if (override && override.nodeId) {
      snapshotNodes = nodes.map((n) =>
        n.id === override.nodeId ? { ...n, text: override.text } : n
      );
    }

    const currentState = { nodes: snapshotNodes, relationships, root, selectedId };
    const currentStateStr = JSON.stringify(currentState);

    if (lastPersistedStateRef.current !== currentStateStr) {
      lastPersistedStateRef.current = currentStateStr;
      // Pass the exact snapshot we just computed so persistence
      // cannot lag behind what the user sees on screen.
      saveMindMap(currentState);
    }
  }, [nodes, relationships, root, selectedId, saveMindMap]);

  // Helper to request a save; actual persistence happens in an effect
  const saveIfChanged = useCallback(() => {
    const now = Date.now();
    // Debounce save requests so a burst of changes (e.g. during
    // login tag updates or fast edits) collapses into a single
    // persistence operation.
    if (now - lastSaveRequestRef.current < 500) {
      return;
    }
    lastSaveRequestRef.current = now;
    setSaveTrigger(t => t + 1);
  }, []);

  // Track whether there are changes in memory that haven't been
  // captured in the last persisted snapshot yet. This lets us
  // warn the user before closing the tab if a save is still
  // pending in memory.
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    const currentStateStr = JSON.stringify({ nodes, relationships, root, selectedId });
    hasUnsavedChangesRef.current = currentStateStr !== lastPersistedStateRef.current;
  }, [nodes, relationships, root, selectedId]);

  // Helper function to calculate distance between two points
  const getDistance = useCallback((pos1, pos2) => {
    if (!pos1 || !pos2) return Infinity;
    const x1 = typeof pos1.x === 'number' ? pos1.x : 0;
    const y1 = typeof pos1.y === 'number' ? pos1.y : 0;
    const x2 = typeof pos2.x === 'number' ? pos2.x : 0;
    const y2 = typeof pos2.y === 'number' ? pos2.y : 0;
    return Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
  }, []);

  // Helper function to get node center position
  const getNodeCenter = useCallback((node) => {
    if (!node) {
      return { x: 0, y: 0 };
    }

    const nodeRef = nodeRefs.current[node.id];
    let width = 200;
    let height = 60; // defaults

    const measuredSize = readNodeLayoutSize(nodeRef, scale);
    if (measuredSize) {
      width = measuredSize.width;
      height = measuredSize.height;
    }

    const baseX = node.position && typeof node.position.x === 'number' ? node.position.x : 0;
    const baseY = node.position && typeof node.position.y === 'number' ? node.position.y : 0;

    return {
      x: baseX + width / 2,
      y: baseY + height / 2,
    };
  }, [scale]);

  // Check if dragged node can be dropped on target (prevent cycles)
  const canReparent = useCallback((draggedId, targetId) => {
    if (draggedId === targetId) return false;

    const descendants = getChildren(draggedId);
    return !descendants.includes(targetId);
  }, [getChildren]);

  // Function to get current children positions for a node
  const getChildrenPositions = useCallback((nodeId) => {
    const childRelations = relationships.filter(rel => rel.parentId === nodeId);
    const children = childRelations.map(rel =>
      nodes.find(child => child.id === rel.childId)
    ).filter(Boolean);

    return children.map(child => ({
      id: child.id,
      x: child.position.x,
      y: child.position.y
    }));
  }, [relationships, nodes]);

  const handleExportImage = useCallback(async () => {
    if (!containerRef.current || typeof window === 'undefined') return;

    try {
      const rect = containerRef.current.getBoundingClientRect();

      const canvas = await html2canvas(document.body, {
        backgroundColor: getThemeCanvasSolidColor(mapTheme, isDarkMode),
        scale: window.devicePixelRatio || 2,
        useCORS: true,
        width: rect.width,
        height: rect.height,
        ignoreElements: (element) => {
          return element.dataset && element.dataset.exportIgnore === 'true';
        },
      });

      const dataUrl = canvas.toDataURL('image/png');

      const titleNode =
        (focusRootId && nodes.find((n) => n.id === focusRootId)) ||
        nodes.find((n) => n.isRoot) ||
        (root && nodes.find((n) => n.id === root));

      const baseNameRaw =
        titleNode && typeof titleNode.text === 'string' && titleNode.text.trim()
          ? titleNode.text.trim()
          : 'mindmap';

      const safeBaseName =
        baseNameRaw
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-_]/g, '')
          .slice(0, 60) || 'mindmap';

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${safeBaseName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export mind map image:', err);
    }
  }, [isDarkMode, mapTheme, nodes, root, focusRootId]);

  // Capture current map view and upload as preview thumbnail
  const handleSaveThumbnail = useCallback(async (opts = {}) => {
    const silent = !!(opts && opts.silent);
    try {
      if (!isLoggedIn || !mindmapIdForYjs) {
        if (!silent) showNotification('Sign in to save a preview thumbnail.', 'error');
        return;
      }

      // Optional: only allow owner to upload
      const ownerUserId = remoteMapData && remoteMapData.ownerUserId ? remoteMapData.ownerUserId : null;
      if (ownerUserId && currentUserId && ownerUserId !== currentUserId) {
        if (!silent) showNotification('Only the map owner can update the preview.', 'error');
        return;
      }

      if (!containerRef.current) {
        if (!silent) showNotification('Map container not ready to capture.', 'error');
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();

      const canvas = await html2canvas(document.body, {
        backgroundColor: getThemeCanvasSolidColor(mapTheme, isDarkMode),
        scale: window.devicePixelRatio || 2,
        useCORS: true,
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
        ignoreElements: (element) => {
          return element.dataset && element.dataset.exportIgnore === 'true';
        },
      });

      // Downscale to a max dimension of 512px, preserving aspect ratio
      const srcW = canvas.width;
      const srcH = canvas.height;
      const maxDim = 512;
      const scaleFactor = Math.min(1, maxDim / Math.max(srcW, srcH));
      const outW = Math.max(1, Math.round(srcW * scaleFactor));
      const outH = Math.max(1, Math.round(srcH * scaleFactor));

      const outCanvas = document.createElement('canvas');
      outCanvas.width = outW;
      outCanvas.height = outH;
      const ctx = outCanvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(canvas, 0, 0, srcW, srcH, 0, 0, outW, outH);

      // Encode as PNG to match backend expectations
      const dataUrl = outCanvas.toDataURL('image/png');

      await uploadMindMapThumbnail(mindmapIdForYjs, dataUrl);
      if (!silent) showNotification('Preview thumbnail saved.', 'success');
    } catch (err) {
      console.error('Failed to save preview thumbnail:', err);
      if (!silent) showNotification('Could not save thumbnail. Try again.', 'error');
    }
  }, [isLoggedIn, mindmapIdForYjs, remoteMapData, currentUserId, isDarkMode, mapTheme, showNotification]);

  // Auto-capture every 30s when owner is viewing, only if content changed
  const lastThumbnailSignatureRef = useRef(null);

  const computeThumbnailSignature = useCallback(() => {
    try {
      const normNodes = (nodes || [])
        .map((n) => ({
          id: n.id,
          text: typeof n.text === 'string' ? n.text : '',
          x: n.position && typeof n.position.x === 'number' ? n.position.x : 0,
          y: n.position && typeof n.position.y === 'number' ? n.position.y : 0,
          color: n.color || null,
          isRoot: !!n.isRoot,
        }))
        .sort((a, b) => String(a.id).localeCompare(String(b.id)));

      const normRels = (relationships || [])
        .map((r) => ({ parentId: r.parentId, childId: r.childId }))
        .sort((a, b) =>
          String(a.parentId).localeCompare(String(b.parentId)) ||
          String(a.childId).localeCompare(String(b.childId))
        );

      return JSON.stringify({ nodes: normNodes, relationships: normRels, root: root || null });
    } catch {
      return null;
    }
  }, [nodes, relationships, root]);

  useEffect(() => {
    if (!isLoggedIn || !mindmapIdForYjs) return;
    // Only the owner auto-captures to prevent competing uploads
    const ownerUserId = remoteMapData && remoteMapData.ownerUserId ? remoteMapData.ownerUserId : null;
    if (ownerUserId && currentUserId && ownerUserId !== currentUserId) return;

    let timerId = null;
    const tick = async () => {
      try {
        if (typeof document !== 'undefined' && document.hidden) return;
        if (!isInitialized) return;

        const sig = computeThumbnailSignature();
        if (!sig) return;
        if (sig !== lastThumbnailSignatureRef.current) {
          lastThumbnailSignatureRef.current = sig;
          await handleSaveThumbnail({ silent: true });
        }
      } catch (e) {
        // swallow errors to keep interval alive
        console.warn('Auto-capture tick failed:', e);
      }
    };

    // Kick off after a short delay so initial render settles
    timerId = setInterval(tick, 30000);
    // Run one initial tick after initialization
    const initId = setTimeout(tick, 3000);
    return () => {
      clearInterval(timerId);
      clearTimeout(initId);
    };
  }, [isLoggedIn, mindmapIdForYjs, remoteMapData, currentUserId, isInitialized, computeThumbnailSignature, handleSaveThumbnail]);

  const handleExportData = useCallback(() => {
    if (!isLoggedIn || !yActions || !yActions.exportSnapshot) return;

    try {
      const snapshot = yActions.exportSnapshot();
      if (!snapshot || !Array.isArray(snapshot.nodes)) return;

      const state = new MindMapState({
        nodes: snapshot.nodes,
        relationships: snapshot.relationships || [],
        root: snapshot.root || null,
        selectedId: snapshot.root || null,
      });

      const titleNode =
        (focusRootId && nodes.find((n) => n.id === focusRootId)) ||
        nodes.find((n) => n.isRoot) ||
        (root && nodes.find((n) => n.id === root));

      const baseNameRaw =
        titleNode && typeof titleNode.text === 'string' && titleNode.text.trim()
          ? titleNode.text.trim()
          : 'mindmap';

      const safeBaseName =
        baseNameRaw
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-_]/g, '')
          .slice(0, 60) || 'mindmap';

      FileExporter.exportToFile(state, EXPORT_FORMATS.JSON, {
        filename: safeBaseName,
        includeMetadata: true,
        pretty: true,
      });
    } catch (err) {
      console.error('Failed to export mind map data:', err);
    }
  }, [isLoggedIn, yActions, nodes, root, focusRootId]);

  const handleImportData = useCallback(async () => {
    if (!isLoggedIn || !yActions) return;

    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,.xml,.csv,.md,.markdown,.txt';

      input.onchange = async () => {
        const file = input.files && input.files[0];
        if (!file) return;

        try {
          const validation = FileImporter.validateFile(file);
          if (!validation.isValid) {
            console.warn('Import file validation failed:', validation.errors);
            return;
          }

          const importedState = await FileImporter.importFromFile(file);
          if (!importedState) return;

          const plain = importedState.toJSON();
          const nodesArray = Array.isArray(plain.nodes) ? plain.nodes : [];
          const relsArray = Array.isArray(plain.relationships) ? plain.relationships : [];
          const rootId = plain.root || null;

          // Clear existing document and apply imported content
          if (yActions.replaceDocument) {
            yActions.replaceDocument({
              nodes: nodesArray,
              relationships: relsArray,
              root: rootId,
            });
          } else if (yActions.addNode && yActions.deleteNode && yActions.addRelationship && yActions.setRoot) {
            // Fallback: naive replace by deleting all current nodes then inserting imported ones
            if (Array.isArray(nodes)) {
              nodes.forEach((n) => {
                if (n && n.id) yActions.deleteNode(n.id);
              });
            }

            nodesArray.forEach((node) => {
              if (!node || !node.id) return;
              yActions.addNode(node);
            });

            relsArray.forEach((rel) => {
              if (!rel || !rel.parentId || !rel.childId) return;
              yActions.addRelationship(rel.parentId, rel.childId);
            });

            if (rootId) {
              yActions.setRoot(rootId);
            }
          }
        } catch (err) {
          console.error('Failed to import mind map data:', err);
        }
      };

      input.click();
    } catch (err) {
      console.error('Failed to start import:', err);
    }
  }, [isLoggedIn, yActions, nodes]);

  const exportDataHandlerRef = useRef(handleExportData);
  const importDataHandlerRef = useRef(handleImportData);
  const setMapThemeRef = useRef(setMapTheme);
  exportDataHandlerRef.current = handleExportData;
  importDataHandlerRef.current = handleImportData;
  setMapThemeRef.current = setMapTheme;

  useEffect(() => {
    if (typeof onRegisterMapFileActions !== 'function') return undefined;

    onRegisterMapFileActions({
      exportCurrentMap: isLoggedIn
        ? () => exportDataHandlerRef.current()
        : null,
      importIntoCurrentMap: isLoggedIn && canWriteMap
        ? () => importDataHandlerRef.current()
        : null,
      mapTheme: {
        activeThemeId,
        canChangeTheme: canManageMapMetadata,
        setTheme: (themeId) => setMapThemeRef.current(themeId),
      },
      appearance: {
        isDarkMode,
        toggleDarkMode,
      },
      openSearch: openMapSearch,
      search: {
        query: searchQuery,
        isOpen: isSearchOpen,
        results: searchResults,
        activeIndex: searchActiveIndex,
        open: openMapSearch,
        close: closeMapSearch,
        setQuery: updateMapSearchQuery,
        setActiveIndex: setSearchActiveIndex,
        moveActive: moveMapSearchSelection,
        selectActive: selectActiveMapSearchResult,
        selectResult: handleSearchResult,
      },
    });

    return () => onRegisterMapFileActions(null);
  }, [
    activeThemeId,
    canManageMapMetadata,
    canWriteMap,
    closeMapSearch,
    handleSearchResult,
    isDarkMode,
    isLoggedIn,
    isSearchOpen,
    moveMapSearchSelection,
    onRegisterMapFileActions,
    openMapSearch,
    searchActiveIndex,
    searchQuery,
    searchResults,
    selectActiveMapSearchResult,
    toggleDarkMode,
    updateMapSearchQuery,
  ]);

  const getRuntimeNodeSizes = useCallback(() => {
    const sizes = new Map(measuredNodeSizesRef.current);
    Object.entries(nodeRefs.current).forEach(([nodeId, element]) => {
      const size = readNodeLayoutSize(element, scale);
      if (size) sizes.set(nodeId, size);
    });
    return sizes;
  }, [scale]);

  const executeMoveNodesCommand = useCallback((command, options = {}) => {
    if (!command) return null;
    if (isLoggedIn && yActions?.moveNodes) {
      return yActions.moveNodes(command, {
        lastEditedBy: currentUserId,
        isolate: !!options.isolate,
      });
    }
    return applyMoveNodesCommand(command);
  }, [applyMoveNodesCommand, currentUserId, isLoggedIn, yActions]);

  const queueMoveNodesCommand = useCallback((command) => {
    const frameState = dragFrameRef.current;
    frameState.pendingCommand = command;
    if (frameState.rafId !== null) return;

    const requestFrame = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (callback) => setTimeout(callback, 0);
    frameState.rafId = requestFrame(() => {
      frameState.rafId = null;
      const pending = frameState.pendingCommand;
      frameState.pendingCommand = null;
      if (pending) executeMoveNodesCommand(pending);
    });
  }, [executeMoveNodesCommand]);

  const clearQueuedMoveCommand = useCallback(() => {
    const frameState = dragFrameRef.current;
    if (frameState.rafId !== null) {
      if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(frameState.rafId);
      } else {
        clearTimeout(frameState.rafId);
      }
    }
    frameState.rafId = null;
    frameState.pendingCommand = null;
  }, []);

  useEffect(() => () => clearQueuedMoveCommand(), [clearQueuedMoveCommand]);

  const stopCollisionSlide = useCallback(() => {
    if (layoutTransitionTimerRef.current !== null) {
      clearTimeout(layoutTransitionTimerRef.current);
      layoutTransitionTimerRef.current = null;
    }
    setLayoutTransitionNodeIds(new Set());
  }, []);

  const startCollisionSlide = useCallback((command) => {
    if (!command?.collisionResolved || !Array.isArray(command.positions)) {
      stopCollisionSlide();
      return false;
    }
    if (typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      stopCollisionSlide();
      return false;
    }

    if (layoutTransitionTimerRef.current !== null) {
      clearTimeout(layoutTransitionTimerRef.current);
    }
    setLayoutTransitionNodeIds(new Set(command.positions.map((entry) => entry.id)));
    layoutTransitionTimerRef.current = setTimeout(() => {
      layoutTransitionTimerRef.current = null;
      setLayoutTransitionNodeIds(new Set());
    }, COLLISION_SLIDE_MS + 40);
    return true;
  }, [stopCollisionSlide]);

  useEffect(() => () => {
    if (layoutTransitionTimerRef.current !== null) {
      clearTimeout(layoutTransitionTimerRef.current);
    }
  }, []);

  const executeReparentSubtreeCommand = useCallback((command) => {
    if (!command) return null;
    if (isLoggedIn && yActions?.reparentSubtree) {
      return yActions.reparentSubtree(command, { lastEditedBy: currentUserId });
    }
    return applyReparentSubtreeCommand(command);
  }, [applyReparentSubtreeCommand, currentUserId, isLoggedIn, yActions]);

  const screenPointToCanvas = useCallback((point) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    return screenToWorld(point, containerRect, { scale, offset });
  }, [offset, scale]);

  // Handle node drag start
  const handleNodeDragStart = useCallback((nodeId, pointer = null) => {
    // Save state at the start of a drag so the whole drag
    // gesture can be undone in a single step
    saveCurrentState();
    stopCollisionSlide();
    setDragPositionPreview(new Map());

    setDraggedNodeId(nodeId);
    setHighlightedNodeId(null);
    setReparentCandidate(prev => ({ ...prev, visible: false }));
    reparentTargetRef.current = null;

    const draggedNode = nodes.find((node) => node.id === nodeId);
    dragGestureRef.current = {
      nodeId,
      latestPosition: draggedNode?.position ? { ...draggedNode.position } : null,
      nodes: nodes.map((node) => ({
        ...node,
        position: node.position ? { ...node.position } : { x: 0, y: 0 },
      })),
      relationships: relationships.map((relationship) => ({ ...relationship })),
    };
    yActions?.beginInteraction?.();

    if (nodeId && nodeId === root && !focusRootId) {
      rootPanDragRef.current = Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)
        ? { x: pointer.clientX, y: pointer.clientY }
        : null;
    }
  }, [saveCurrentState, stopCollisionSlide, root, focusRootId, nodes, relationships, yActions]);

  // Function to get parent node for a given node
  const getParentNodeData = useCallback((nodeId) => {
    const parentRelation = relationships.find(rel => rel.childId === nodeId);
    if (!parentRelation) return null;

    const parentNode = nodes.find(node => node.id === parentRelation.parentId);
    if (!parentNode) return null;

    const baseX = parentNode.position && typeof parentNode.position.x === 'number'
      ? parentNode.position.x
      : 0;
    const baseY = parentNode.position && typeof parentNode.position.y === 'number'
      ? parentNode.position.y
      : 0;

    return {
      id: parentNode.id,
      x: baseX,
      y: baseY,
      text: parentNode.text,
    };
  }, [relationships, nodes]);

  // Handle node drag move
  const handleNodeDragMove = useCallback((nodeId, position, pointer = null) => {
    if (!draggedNodeId || draggedNodeId !== nodeId) return;

    const draggedNode = getNode(draggedNodeId);
    if (!draggedNode) return;

    if (dragGestureRef.current?.nodeId === nodeId) {
      dragGestureRef.current.latestPosition = { ...position };
    }

    if (nodeId === root && !focusRootId) {
      const lastPointer = rootPanDragRef.current;
      if (lastPointer && Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)) {
        panBy(pointer.clientX - lastPointer.x, pointer.clientY - lastPointer.y);
      }
      if (Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)) {
        rootPanDragRef.current = { x: pointer.clientX, y: pointer.clientY };
      }
      reparentTargetRef.current = null;
      setHighlightedNodeId(null);
      return;
    }

    if (focusRootId && nodeId === focusRootId) {
      reparentTargetRef.current = null;
      setHighlightedNodeId(null);
      return;
    }

    const runtimeSizes = getRuntimeNodeSizes();
    const draggedSize = runtimeSizes.get(draggedNodeId) || {
      width: NODE_WIDTH_GUESS,
      height: NODE_HEIGHT_GUESS,
    };
    const draggedRect = { ...position, ...draggedSize };
    let newHighlighted = null;
    let potentialTarget = null;
    let closestDistance = Infinity;

    // Select the closest valid node whose rendered rectangle overlaps the
    // dragged node. This remains accurate for large cards and zoom levels.
    for (const node of nodes) {
      if (node.id === draggedNodeId) continue;
      if (!canReparent(draggedNodeId, node.id)) continue;

      const targetSize = runtimeSizes.get(node.id) || {
        width: NODE_WIDTH_GUESS,
        height: NODE_HEIGHT_GUESS,
      };
      const targetRect = { ...node.position, ...targetSize };
      if (!topLeftRectsOverlap(draggedRect, targetRect, 0)) continue;
      const draggedCenter = {
        x: position.x + draggedSize.width / 2,
        y: position.y + draggedSize.height / 2,
      };
      const nodeCenter = getNodeCenter(node);
      const distance = getDistance(draggedCenter, nodeCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        newHighlighted = node.id;
        potentialTarget = node;
      }
    }

    setHighlightedNodeId(newHighlighted);
    reparentTargetRef.current = potentialTarget;

    // Show a compact hierarchy badge above the candidate parent. Releasing
    // commits the reparent; moving away clears the candidate.
    if (potentialTarget && (
      !reparentCandidate.visible || reparentCandidate.targetNode?.id !== potentialTarget.id
    )) {
      const targetSize = runtimeSizes.get(potentialTarget.id) || {
        width: NODE_WIDTH_GUESS,
        height: NODE_HEIGHT_GUESS,
      };
      const indicatorPosition = {
        x: potentialTarget.position.x + targetSize.width / 2,
        y: potentialTarget.position.y - 8,
      };

      setReparentCandidate({
        visible: true,
        draggedNode: draggedNode,
        targetNode: potentialTarget,
        position: indicatorPosition,
      });
    } else if (!potentialTarget && reparentCandidate.visible) {
      setReparentCandidate(prev => ({ ...prev, visible: false }));
    }
  }, [
    draggedNodeId,
    nodes,
    getNode,
    canReparent,
    getNodeCenter,
    getDistance,
    getRuntimeNodeSizes,
    reparentCandidate.visible,
    reparentCandidate.targetNode?.id,
    root,
    focusRootId,
    panBy,
  ]);

  // Handle node drag end
  const handleNodeDragEnd = useCallback(() => {
    setDraggedNodeId(null);
    setHighlightedNodeId(null);
    rootPanDragRef.current = null;
  }, []);

  const commitReparentDrop = useCallback((nodeId, newParentId) => {
    try {
      const command = buildReparentSubtreeCommand({
        nodeId,
        newParentId,
        nodes,
        relationships,
        nodeSizes: getRuntimeNodeSizes(),
      });
      startCollisionSlide(command);
      setDragPositionPreview(createDragPositionPreview(command));
      executeReparentSubtreeCommand(command);
      return true;
    } catch (error) {
      console.error('Failed to reparent node:', error);
      setDragPositionPreview(new Map());
      showNotification(error.message || 'Could not reparent that node.', 'error');
      return false;
    }
  }, [
    nodes,
    relationships,
    getRuntimeNodeSizes,
    executeReparentSubtreeCommand,
    startCollisionSlide,
    showNotification,
  ]);

  // Keep the last live position snapshot visible until the underlying local or
  // collaborative graph state reaches it. This avoids a one-frame snap-back at
  // pointer release without allowing stale previews to outlive the gesture.
  useEffect(() => {
    if (draggedNodeId || reparentCandidate.visible) return;
    setDragPositionPreview((current) => (
      current.size > 0 && dragPreviewMatchesNodes(current, nodes) ? new Map() : current
    ));
  }, [draggedNodeId, nodes, reparentCandidate.visible]);

  // Action handlers with undo state management
  const executeCreateNodeCommand = useCallback((command) => {
    if (!command) return null;
    saveCurrentState();

    if (isLoggedIn && yActions && typeof yActions.createNodeWithRelationship === 'function') {
      if (command.node.autoFocus) {
        pendingAutoFocusNodeIdRef.current = command.node.id;
      }
      yActions.createNodeWithRelationship(command);
    } else {
      insertNodeWithRelationship(command);
      saveIfChanged();
    }

    expandBranch(command.relationship.parentId);
    setSelectedId(command.node.id);
    return command.node.id;
  }, [
    isLoggedIn,
    yActions,
    insertNodeWithRelationship,
    saveCurrentState,
    saveIfChanged,
    expandBranch,
    setSelectedId,
  ]);

  const handleAddChildNode = useCallback((parentIdOverride = null, preferredPosition = null) => {
    const explicitParentId = typeof parentIdOverride === 'string' ? parentIdOverride : null;
    const base = (explicitParentId && getNode(explicitParentId))
      || getSelectedNode()
      || (root && getNode(root))
      || nodes.find((node) => node.isRoot);
    if (!base) return null;

    const command = buildCreateChildCommand({
      id: crypto.randomUUID(),
      parentId: base.id,
      nodes,
      relationships,
      preferredPosition,
      nodeSizes: getRuntimeNodeSizes(),
      color: base.color || COLORS[0]?.gradient || null,
      userId: currentUserId || null,
      creatorName: currentUserName || null,
    });

    return executeCreateNodeCommand(command);
  }, [
    getSelectedNode,
    getNode,
    root,
    nodes,
    relationships,
    currentUserId,
    currentUserName,
    getRuntimeNodeSizes,
    executeCreateNodeCommand,
  ]);

  const handleAddSiblingNode = useCallback((selectedNodeIdOverride = null) => {
    const explicitNodeId = typeof selectedNodeIdOverride === 'string' ? selectedNodeIdOverride : null;
    const selectedNode = (explicitNodeId && getNode(explicitNodeId)) || getSelectedNode();
    if (!selectedNode) return null;

    try {
      const command = buildCreateSiblingCommand({
        id: crypto.randomUUID(),
        selectedNodeId: selectedNode.id,
        nodes,
        relationships,
        nodeSizes: getRuntimeNodeSizes(),
        color: selectedNode.color || COLORS[0]?.gradient || null,
        userId: currentUserId || null,
        creatorName: currentUserName || null,
      });
      return executeCreateNodeCommand(command);
    } catch (error) {
      // Root nodes intentionally cannot have siblings.
      showNotification(error.message, 'error');
      return null;
    }
  }, [
    getSelectedNode,
    getNode,
    nodes,
    relationships,
    currentUserId,
    currentUserName,
    getRuntimeNodeSizes,
    executeCreateNodeCommand,
    showNotification,
  ]);

  const executeDeleteSubtreeCommand = useCallback((command) => {
    if (!command) return false;
    saveCurrentState();
    if (isLoggedIn) {
      if (!yActions?.deleteSubtree) throw new Error('The collaborative map is still syncing');
      yActions.deleteSubtree(command);
    } else {
      applyDeleteSubtreeCommand(command);
      saveIfChanged();
    }

    const deletingIds = new Set(command.nodeIds);
    setCollapsedNodeIds((previous) => new Set(
      [...previous].filter((nodeId) => !deletingIds.has(nodeId))
    ));
    setTemporarilyExpandedNodeIds((previous) => new Set(
      [...previous].filter((nodeId) => !deletingIds.has(nodeId))
    ));
    setFocusHistory((previous) => pruneFocusHistory(previous, deletingIds));
    if (focusRootId && deletingIds.has(focusRootId)) {
      updateFocusView(command.parentId && command.parentId !== root ? command.parentId : null, { fit: false });
    }
    setSelectedId(command.nextSelectedId || null);
    return true;
  }, [
    applyDeleteSubtreeCommand,
    focusRootId,
    isLoggedIn,
    root,
    saveCurrentState,
    saveIfChanged,
    setSelectedId,
    updateFocusView,
    yActions,
  ]);

  const handleRemoveNode = useCallback((nodeIdOverride = null) => {
    const explicitNodeId = typeof nodeIdOverride === 'string' ? nodeIdOverride : null;
    const selectedNode = (explicitNodeId && getNode(explicitNodeId)) || getSelectedNode();
    if (!selectedNode) return false;

    try {
      const command = buildDeleteSubtreeCommand({
        nodeId: selectedNode.id,
        rootId: root,
        nodes,
        relationships,
        actorUserId: currentUserId || null,
      });
      if (command.nodeIds.length > 1 && typeof window !== 'undefined') {
        const confirmed = window.confirm(
          `Delete this branch and its ${command.nodeIds.length - 1} descendant${command.nodeIds.length === 2 ? '' : 's'}?`
        );
        if (!confirmed) return false;
      }
      return executeDeleteSubtreeCommand(command);
    } catch (error) {
      showNotification(error.message || 'This branch could not be deleted.', 'error');
      return false;
    }
  }, [
    currentUserId,
    executeDeleteSubtreeCommand,
    getNode,
    getSelectedNode,
    nodes,
    relationships,
    root,
    showNotification,
  ]);

  const handleCollapseNode = useCallback(() => {
    const selectedNode = getSelectedNode();
    if (selectedNode) {
      collapseBranch(selectedNode.id);
    }
  }, [collapseBranch, getSelectedNode]);

  const handleExpandNode = useCallback(() => {
    const selectedNode = getSelectedNode();
    if (selectedNode) {
      expandBranch(selectedNode.id);
    }
  }, [expandBranch, getSelectedNode]);

  const restoreLocalHistoryState = useCallback((historyState, action) => {
    if (!historyState) return;
    loadMindMapFromData(historyState);
    // Force the next real edit to record this restored state and clear any
    // obsolete forward branch. The hook itself retains duplicate protection.
    lastSavedStateRef.current = null;
    if (historyState.viewport && typeof setViewport === 'function') {
      setViewport(historyState.viewport);
    }
    console.log(`${action} complete, restored history state`);
  }, [loadMindMapFromData, setViewport]);

  const handleUndo = useCallback(() => {
    // Logged-in (Yjs) path: delegate undo to the Yjs UndoManager so
    // history is consistent across collaborators.
    if (isLoggedIn && yActions && typeof yActions.undo === 'function') {
      yActions.undo();
      return;
    }

    // Guest / legacy path: restore from local snapshot history.
    const previousState = localUndo(getCurrentHistoryState());
    restoreLocalHistoryState(previousState, 'Undo');
  }, [isLoggedIn, yActions, localUndo, getCurrentHistoryState, restoreLocalHistoryState]);

  const handleRedo = useCallback(() => {
    if (isLoggedIn && yActions && typeof yActions.redo === 'function') {
      yActions.redo();
      return;
    }

    const nextState = localRedo(getCurrentHistoryState());
    restoreLocalHistoryState(nextState, 'Redo');
  }, [isLoggedIn, yActions, localRedo, getCurrentHistoryState, restoreLocalHistoryState]);

  // Combined canUndo that prefers Yjs history when logged in and
  // falls back to the local stack for guest mode.
  const canUndo = useCallback(() => {
    if (isLoggedIn && yActions && typeof yActions.canUndo === 'function') {
      return yActions.canUndo();
    }
    return localCanUndo();
  }, [isLoggedIn, yActions, localCanUndo]);

  const canRedo = useCallback(() => {
    if (isLoggedIn && yActions && typeof yActions.canRedo === 'function') {
      return yActions.canRedo();
    }
    return localCanRedo();
  }, [isLoggedIn, yActions, localCanRedo]);

  const openSelectedNodeMenu = useCallback(() => {
    const selectedNode = getSelectedNode();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const nodeRect = selectedNode
      ? nodeRefs.current[selectedNode.id]?.getBoundingClientRect?.()
      : null;
    if (!selectedNode || !containerRect || !nodeRect) return;
    const viewportPoint = clientToViewport(
      {
        x: nodeRect.left + Math.min(nodeRect.width, 28),
        y: nodeRect.bottom + 8,
      },
      containerRect
    );
    contextMenuOpenedAtRef.current = Date.now();
    setContextMenu({
      visible: true,
      nodeId: selectedNode.id,
      x: viewportPoint.x,
      y: viewportPoint.y,
    });
  }, [getSelectedNode]);

  // Initialize keyboard shortcuts
  const { handleKeyDown } = useKeyboardShortcuts({
    selectedNode: getSelectedNode(),
    onAddChild: canWriteMap ? handleAddChildNode : undefined,
    onAddSibling: canWriteMap ? handleAddSiblingNode : undefined,
    onSelectParent: () => {
      const selectedNode = getSelectedNode();
      if (selectedNode) {
        const parent = getParentNode(selectedNode.id);
        if (parent) {
          revealNode(parent.id);
          if (focusRootId && !getBranchNodeIds(focusRootId, relationships).has(parent.id)) {
            updateFocusView(null, { fit: false });
          }
          setSelectedId(parent.id);
          focusNodeIfOutOfView(parent.id);
        }
      }
    },
    onFocusNode: canWriteMap ? (nodeId) => {
      if (nodeId) {
        const nodeRef = nodeRefs.current[nodeId];
        if (nodeRef && nodeRef.startEditing) {
          nodeRef.startEditing();
        }
      }
    } : undefined,
    onUndo: canWriteMap ? handleUndo : undefined,
    onRedo: canWriteMap ? handleRedo : undefined,
    onRemoveNode: canWriteMap ? handleRemoveNode : undefined,
    onCollapseNode: handleCollapseNode,
    onExpandNode: handleExpandNode,
    onFitVisible: handleFitVisible,
    onFitSelection: handleFitSelection,
    onOpenSearch: () => openMapSearch({ select: true }),
    onOpenNodeMenu: openSelectedNodeMenu,
    onFocusBack: focusRootId && focusHistory.index > 0
      ? () => navigateFocusHistory(-1)
      : undefined,
    onDeleteNode: canWriteMap ? () => {
      // Check if any node is currently being edited
      const editingNode = Object.keys(nodeRefs.current).find(nodeId => {
        const nodeRef = nodeRefs.current[nodeId];
        return nodeRef && nodeRef.isEditing && nodeRef.isEditing();
      });

      // Only delete if no node is being edited
      if (!editingNode) {
        handleRemoveNode();
      }
    } : undefined
  });

  // Handle auto-focusing new nodes
  useEffect(() => {
    const pendingNodeId = pendingAutoFocusNodeIdRef.current;
    const nodeToFocus = isLoggedIn
      ? nodes.find((node) => node.id === pendingNodeId)
      : nodes.find((node) => node.autoFocus);
    if (nodeToFocus) {
      const nodeRef = nodeRefs.current[nodeToFocus.id];
      if (nodeRef && nodeRef.startEditing) {
        nodeRef.startEditing();
        pendingAutoFocusNodeIdRef.current = null;
        if (!isLoggedIn) clearAutoFocus();
      }
    }
  }, [nodes, clearAutoFocus, isLoggedIn]);

  // Clear auto-focus animation flags
  useEffect(() => {
    if (nodes.some(n => n.animate)) {
      const timeout = setTimeout(() => {
        clearAutoFocus();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [nodes, clearAutoFocus]);

  // Initialize with default data on mount for guest mode only.
  // Logged-in users load state from Yjs instead of legacy snapshots.
  useEffect(() => {
    if (isLoggedIn) return;

    if (remoteMapData && Array.isArray(remoteMapData.nodes) && remoteMapData.nodes.length > 0) {
      console.log('[DEBUG] Initializing map from remoteMapData');
      loadMindMapFromData(remoteMapData);
    } else {
      loadMindMap();
    }
    hasLoadedRef.current = true;
  }, [isLoggedIn, loadMindMap, loadMindMapFromData, remoteMapData]);

  // Once the mind map has loaded and nodes exist, wait for a
  // frame so scale/layout can settle, then reveal the map.
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!nodes.length) return;

    const id = window.requestAnimationFrame(() => {
      setIsInitialized(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [nodes.length]);

  // Persist when a debounced save has been requested and state changed
  useEffect(() => {
    if (saveTrigger === 0) return;
    persistNow();
  }, [saveTrigger, persistNow]);

  // Removed beforeunload warning: Yjs sync persists changes continuously,
  // so we no longer block page unload with a confirmation dialog.

  // On initial load, if a `?node=` query param is present and matches
  // an existing node, focus on that node and its descendants.
  useEffect(() => {
    if (urlNodeHandledRef.current) return;
    if (!nodes.length) return;
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const urlNodeId = params.get('node');
    if (!urlNodeId) {
      urlNodeHandledRef.current = true;
      if (onBaseNodeChange) {
        onBaseNodeChange(null);
      }
      return;
    }

    const exists = nodes.some(n => n.id === urlNodeId);
    if (!exists) {
      console.warn('URL node id not found in current map:', urlNodeId);
      urlNodeHandledRef.current = true;
      if (onBaseNodeChange) {
        onBaseNodeChange(null);
      }
      return;
    }

    updateFocusView(urlNodeId);
    urlNodeHandledRef.current = true;
  }, [nodes, onBaseNodeChange, updateFocusView]);

  // Helper functions for UI
  const selectedNodePosition = () => {
    const node = getSelectedNode();
    if (!node) return { x: 0, y: 0 };

    // Get the actual node size from the DOM if available
    const nodeRef = nodeRefs.current[node.id];
    let nodeWidth = 200;

    const measuredSize = readNodeLayoutSize(nodeRef, scale);
    if (measuredSize) nodeWidth = measuredSize.width;

    return worldToViewport({
      // Position to the top-right of the node
      x: node.position.x + nodeWidth + 10, // 10px to the right
      y: node.position.y - 10 // 10px above
    }, { scale, offset });
  };

  const handleNodeSelect = (nodeId) => {
    setSelectedId(nodeId);
    // Hide any open context menu when selection changes via click
    if (contextMenu.visible && contextMenu.nodeId !== nodeId) {
      setContextMenu(prev => ({ ...prev, visible: false }));
    }
  };

  const handleNodePositionChange = useCallback((nodeId, position) => {
    const isGlobalRoot = Boolean(root && nodeId === root);
    const isFocusedRootDrag = Boolean(focusRootId && nodeId === focusRootId);

    if (isGlobalRoot && !isFocusedRootDrag) {
      return;
    }

    const gesture = dragGestureRef.current?.nodeId === nodeId
      ? dragGestureRef.current
      : {
          nodeId,
          nodes,
          relationships,
        };

    let commandNodeId = nodeId;
    let targetPosition = position;
    if (isFocusedRootDrag) {
      const focused = gesture.nodes.find((node) => node.id === focusRootId);
      const rootNode = gesture.nodes.find((node) => node.id === root);
      if (focused && rootNode) {
        commandNodeId = root;
        targetPosition = {
          x: rootNode.position.x + position.x - focused.position.x,
          y: rootNode.position.y + position.y - focused.position.y,
        };
        centerNodePrecise(root, { positionOverride: targetPosition });
      }
    }

    if (dragGestureRef.current?.nodeId === nodeId) {
      dragGestureRef.current.finalNodeId = commandNodeId;
      dragGestureRef.current.finalTargetPosition = { ...targetPosition };
    }

    try {
      const command = buildMoveSubtreeCommand({
        nodeId: commandNodeId,
        targetPosition,
        nodes: gesture.nodes,
        relationships: gesture.relationships,
      });
      setDragPositionPreview(createDragPositionPreview(command));
      queueMoveNodesCommand(command);
    } catch (error) {
      console.error('Failed to move node:', error);
    }
  }, [
    root,
    focusRootId,
    nodes,
    relationships,
    centerNodePrecise,
    queueMoveNodesCommand,
  ]);

  const handleNodeDragComplete = useCallback(() => {
    clearQueuedMoveCommand();
    const gesture = dragGestureRef.current;
    const reparentTarget = reparentTargetRef.current;

    // Dragging the global root outside focus mode pans the viewport and does
    // not mutate node positions.
    if (gesture?.nodeId === root && !focusRootId) {
      setDragPositionPreview(new Map());
      yActions?.endInteraction?.();
      dragGestureRef.current = null;
      return;
    }

    if (gesture?.nodeId && reparentTarget?.id) {
      commitReparentDrop(gesture.nodeId, reparentTarget.id);
      dragGestureRef.current = null;
      reparentTargetRef.current = null;
      setReparentCandidate({
        visible: false,
        draggedNode: null,
        targetNode: null,
        position: { x: 0, y: 0 },
      });
      yActions?.endInteraction?.();
      saveIfChanged();
      return;
    }

    const finalNodeId = gesture?.finalNodeId || gesture?.nodeId;
    const finalTargetPosition = gesture?.finalTargetPosition || gesture?.latestPosition;
    if (finalTargetPosition && !reparentTargetRef.current) {
      try {
        const command = buildMoveSubtreeCommand({
          nodeId: finalNodeId,
          targetPosition: finalTargetPosition,
          nodes: gesture.nodes,
          relationships: gesture.relationships,
          pinRoot: !(focusRootId && gesture.nodeId === focusRootId),
        });
        const resolved = focusRootId && gesture.nodeId === focusRootId
          ? command
          : resolveMoveCommandCollisions(command, {
              nodes,
              nodeSizes: getRuntimeNodeSizes(),
            });
        startCollisionSlide(resolved);
        setDragPositionPreview(createDragPositionPreview(resolved));
        executeMoveNodesCommand(resolved);
      } catch (error) {
        console.error('Failed to finalize node movement:', error);
        setDragPositionPreview(new Map());
      }
      dragGestureRef.current = null;
    }

    yActions?.endInteraction?.();
    saveIfChanged();
  }, [
    clearQueuedMoveCommand,
    root,
    focusRootId,
    nodes,
    getRuntimeNodeSizes,
    executeMoveNodesCommand,
    commitReparentDrop,
    startCollisionSlide,
    saveIfChanged,
    yActions,
  ]);

  const scheduleNodeCollisionResolution = useCallback((nodeId) => {
    if (!nodeId) return;
    const scheduleFrame = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (callback) => setTimeout(callback, 0);
    const cancelFrame = typeof window !== 'undefined' && window.cancelAnimationFrame
      ? window.cancelAnimationFrame.bind(window)
      : clearTimeout;

    if (resizeLayoutFrameRef.current !== null) {
      cancelFrame(resizeLayoutFrameRef.current);
    }
    resizeLayoutFrameRef.current = scheduleFrame(() => {
      resizeLayoutFrameRef.current = scheduleFrame(() => {
        resizeLayoutFrameRef.current = null;
        const graph = latestGraphRef.current;
        const node = graph.nodes.find((candidate) => candidate.id === nodeId);
        if (!node?.position) return;
        try {
          const command = buildMoveSubtreeCommand({
            nodeId,
            targetPosition: node.position,
            nodes: graph.nodes,
            relationships: graph.relationships,
          });
          const resolved = resolveMoveCommandCollisions(command, {
            nodes: graph.nodes,
            nodeSizes: getRuntimeNodeSizes(),
          });
          if (resolved.collisionResolved) {
            startCollisionSlide(resolved);
            executeMoveNodesCommand(resolved);
            saveIfChanged();
          }
        } catch (error) {
          console.error('Failed to resolve resized node collision:', error);
        }
      });
    });
  }, [executeMoveNodesCommand, getRuntimeNodeSizes, saveIfChanged, startCollisionSlide]);

  useEffect(() => () => {
    if (resizeLayoutFrameRef.current === null) return;
    if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(resizeLayoutFrameRef.current);
    } else {
      clearTimeout(resizeLayoutFrameRef.current);
    }
  }, []);

  const handleNodeEditComplete = (finalText, nodeId) => {
    // Trigger persistence after editing; undo snapshot is taken
    // when editing starts via saveCurrentState
    if (onBaseNodeChange && focusRootId) {
      const node = getNode(focusRootId);
      onBaseNodeChange(node && node.text ? node.text : null);
    }
    // Save immediately when a node finishes editing so text
    // changes aren't lost even if there are no further actions.
    persistNow(nodeId && typeof finalText === 'string'
      ? { nodeId, text: finalText }
      : undefined
    );
    scheduleNodeCollisionResolution(nodeId);

    // When the edited node is the global root, sync the map title
    // to the backend so shared links and previews stay up to date.
    try {
      if (isLoggedIn && canManageMapMetadata && mindmapIdForYjs && nodeId) {
        const edited = getNode(nodeId);
        const isRootNode = !!(edited && (edited.isRoot || (root && nodeId === root)));
        if (isRootNode) {
          const title = typeof finalText === 'string' ? finalText.trim() : '';
          // Fire-and-forget; persistence of Yjs state already happened above.
          updateMindMapTitle(mindmapIdForYjs, title)
            .then(() => publishMapTitleUpdate({ mapId: mindmapIdForYjs, title }))
            .catch((err) => {
              console.error('Failed to update map title from base node edit:', err);
            });
        }
      }
    } catch (err) {
      console.error('Error while attempting to sync map title:', err);
    }
  };

  const handleColorSelect = (color) => {
    const selectedNode = getSelectedNode();
    if (!selectedNode) {
      setShowPicker(false);
      return;
    }

    const isForeignOwner = !!selectedNode.userId && !!currentUserId && selectedNode.userId !== currentUserId;
    if (isForeignOwner) {
      setShowPicker(false);
      showNotification('You can only change the color of nodes you created.', 'error');
      return;
    }

    saveCurrentState(); // Save before color change
    const canonicalColor = getCanonicalPaletteColor(color);

    // Logged-in (Yjs) path: apply color via Yjs so it
    // syncs across collaborators and survives projection.
    if (isLoggedIn && yActions && yActions.updateNode) {
      const gradient = canonicalColor?.gradient || null;
      yActions.updateNode(selectedNode.id, { color: gradient, lastEditedBy: currentUserId });
    } else {
      // Guest / legacy path: update local React state.
      setNodeColor(selectedNode.id, canonicalColor);
      saveIfChanged();
    }

    setShowPicker(false);
  };

  const handleHome = useCallback(() => {
    updateFocusView(null);
  }, [updateFocusView]);

  const focusBranchInCurrentView = useCallback((nodeId) => {
    const node = getNode(nodeId);
    if (!node) return;
    revealNode(nodeId);
    expandBranch(nodeId);
    updateFocusView(nodeId);
  }, [expandBranch, getNode, revealNode, updateFocusView]);

  // Right-click on a node: open context menu near the click position
  const handleNodeContextMenu = useCallback((nodeId, event) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportPoint = clientToViewport(
      { x: event.clientX, y: event.clientY },
      rect
    );

    setSelectedId(nodeId);
    contextMenuOpenedAtRef.current = Date.now();
    setContextMenu({
      visible: true,
      nodeId,
      x: viewportPoint.x,
      y: viewportPoint.y,
    });
  }, [setSelectedId]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  const openNodeDiscussion = useCallback((nodeId) => {
    if (!canUseNodeDiscussions || !nodeId) return;
    setSelectedId(nodeId);
    nodeDiscussions.openDiscussion(nodeId);
    closeContextMenu();
  }, [
    canUseNodeDiscussions,
    closeContextMenu,
    nodeDiscussions,
    setSelectedId,
  ]);

  const handleContextOpenDiscussion = useCallback(() => {
    openNodeDiscussion(contextMenu.nodeId);
  }, [contextMenu.nodeId, openNodeDiscussion]);

  const handleContextAddChild = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    handleAddChildNode(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, setSelectedId, handleAddChildNode, closeContextMenu]);

  const handleContextAddSibling = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    handleAddSiblingNode(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, setSelectedId, handleAddSiblingNode, closeContextMenu]);

  const handleContextAddTaskChild = useCallback(() => {
    if (!contextMenu.nodeId) return;
    const parentId = contextMenu.nodeId;
    const parentNode = getNode(parentId);
    if (!parentNode) return;

    const command = buildCreateTaskChildCommand({
      id: crypto.randomUUID(),
      parentId,
      nodes,
      relationships,
      nodeSizes: getRuntimeNodeSizes(),
      color: parentNode.color || COLORS[0]?.gradient || null,
      userId: currentUserId || null,
      creatorName: currentUserName || null,
    });
    executeCreateNodeCommand(command);
    closeContextMenu();
  }, [
    contextMenu.nodeId,
    getNode,
    relationships,
    nodes,
    closeContextMenu,
    currentUserId,
    currentUserName,
    getRuntimeNodeSizes,
    executeCreateNodeCommand,
  ]);

  const handleContextDeleteNode = useCallback(() => {
    if (!contextMenu.nodeId) return;
    handleRemoveNode(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, handleRemoveNode, closeContextMenu]);

  const handleContextChangeColor = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    // Offset the color picker a bit from the click to avoid overlap
    setColorPosOverride({ x: contextMenu.x + 8, y: contextMenu.y + 8 });
    setShowPicker(true);
    closeContextMenu();
  }, [contextMenu.nodeId, setSelectedId, setShowPicker, closeContextMenu, contextMenu.x, contextMenu.y]);

  const handleContextChangeEmoji = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    setShowEmojiPicker(true);
    setEmojiPos({ x: contextMenu.x, y: contextMenu.y });
    closeContextMenu();
  }, [contextMenu.nodeId, contextMenu.x, contextMenu.y, setSelectedId, closeContextMenu]);

  const handleContextCreateMindmapLink = useCallback(async () => {
    if (!contextMenu.nodeId) return;
    if (!currentUserId) {
      showNotification('Sign in to create a linked map.', 'error');
      return;
    }
    if (!isLoggedIn || !yActions?.createNodeWithRelationship) {
      showNotification('The map is still syncing. Try again in a moment.', 'error');
      return;
    }

    const parentId = contextMenu.nodeId;
    if (linkedMapCreationRef.current.has(parentId)) return;
    linkedMapCreationRef.current.add(parentId);
    closeContextMenu();

    try {
      const parentNode = getNode(parentId);
      if (!parentNode) throw new Error('The parent node no longer exists');
      const NEW_MAP_TITLE = 'New Map';
      await createLinkedMapPortal({
        title: NEW_MAP_TITLE,
        createMindMap,
        buildCommand: (created) => {
          const latestGraph = latestGraphRef.current;
          const latestParent = latestGraph.nodes.find((node) => node.id === parentId);
          return buildCreateLinkedMapCommand({
            id: crypto.randomUUID(),
            parentId,
            nodes: latestGraph.nodes,
            relationships: latestGraph.relationships,
            nodeSizes: getRuntimeNodeSizes(),
            linkedMindmapId: created.id,
            linkedMindmapUrl: buildMindMapShareUrl(created.id),
            title: NEW_MAP_TITLE,
            color: latestParent?.color || COLORS[0]?.gradient || null,
            userId: currentUserId,
            creatorName: currentUserName || null,
          });
        },
        executeCommand: executeCreateNodeCommand,
      });
      showNotification('Linked map created.', 'success');
    } catch (err) {
      console.error('Failed to create linked mind map:', err);
      showNotification(
        err.createdMindmapId
          ? 'The map was created but could not be linked. It is available from your maps list.'
          : 'Could not create the linked map. Try again.',
        'error'
      );
    } finally {
      linkedMapCreationRef.current.delete(parentId);
    }
  }, [
    contextMenu.nodeId,
    currentUserId,
    isLoggedIn,
    yActions,
    closeContextMenu,
    getNode,
    currentUserName,
    getRuntimeNodeSizes,
    executeCreateNodeCommand,
    showNotification,
  ]);

  const handleMapLinkTitleCommit = useCallback(async (mapId, title) => {
    if (!mapId) return;
    try {
      await updateMindMapTitle(mapId, title || '');
    } catch (err) {
      console.error('Failed to sync map title from link node edit:', err);
    }
  }, []);

  useEffect(() => subscribeToMapTitleUpdates(({ mapId, title }) => {
    const linkedNodes = latestGraphRef.current.nodes.filter(
      (node) => node.embeddedMindmapId === mapId
    );
    if (linkedNodes.length === 0) return;

    if (isLoggedIn && canWriteMap && yActions?.updateNode) {
      linkedNodes.forEach((node) => {
        const patch = buildLinkedMapTitlePatch(node, {
          mapId,
          title,
          currentUserId,
        });
        if (patch) yActions.updateNode(node.id, patch);
      });
      return;
    }

    setNodes((previous) => previous.map((node) => {
      const patch = buildLinkedMapTitlePatch(node, {
        mapId,
        title,
        currentUserId,
      });
      return patch ? { ...node, ...patch } : node;
    }));
    saveIfChanged();
  }), [canWriteMap, currentUserId, isLoggedIn, saveIfChanged, setNodes, yActions]);

  const handleContextFocusBranch = useCallback(() => {
    if (!contextMenu.nodeId) return;
    focusBranchInCurrentView(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, focusBranchInCurrentView, closeContextMenu]);

  const handleContextOpenBranch = useCallback(() => {
    if (!contextMenu.nodeId) return;
    const nodeId = contextMenu.nodeId;
    let fullUrl = `${window.location.origin}${window.location.pathname}`;
    try {
      const current = new URL(window.location.href);
      const params = new URLSearchParams(current.search);
      params.set('node', nodeId);
      const query = params.toString();
      fullUrl = `${window.location.origin}${current.pathname}${query ? `?${query}` : ''}`;
    } catch {
      // Fallback to basic node-only link
      fullUrl = `${window.location.origin}${window.location.pathname}?node=${encodeURIComponent(nodeId)}`;
    }

    window.open(fullUrl, '_blank', 'noopener,noreferrer');
    closeContextMenu();
  }, [contextMenu.nodeId, closeContextMenu]);

  const handleContextCopyLink = useCallback(() => {
    if (!contextMenu.nodeId) return;
    const nodeId = contextMenu.nodeId;
    let fullUrl = `${window.location.origin}${window.location.pathname}`;
    try {
      const current = new URL(window.location.href);
      const params = new URLSearchParams(current.search);
      params.set('node', nodeId);
      const query = params.toString();
      fullUrl = `${window.location.origin}${current.pathname}${query ? `?${query}` : ''}`;
    } catch {
      // Fallback to basic node-only link
      fullUrl = `${window.location.origin}${window.location.pathname}?node=${encodeURIComponent(nodeId)}`;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullUrl).then(() => {
        console.log('Link copied to clipboard:', fullUrl);
      }).catch((err) => {
        console.error('Failed to copy link, showing alert instead:', err);
        // Fallback
        alert(fullUrl);
      });
    } else {
      alert(fullUrl);
    }

    closeContextMenu();
  }, [contextMenu.nodeId, closeContextMenu]);

  const handleContextToggleTask = useCallback(() => {
    if (!contextMenu.nodeId) return;
    const nodeId = contextMenu.nodeId;
    saveCurrentState();

    // Logged-in (Yjs) path: flip task flags via Yjs using the
    // current projected node so it syncs across collaborators.
    if (isLoggedIn && yActions && yActions.updateNode) {
      const existing = getNode(nodeId);
      const currentlyTask = !!(existing && existing.isTask);
      const nextIsTask = !currentlyTask;
      const nextTaskDone = nextIsTask ? false : false; // turning off clears done

      yActions.updateNode(nodeId, {
        isTask: nextIsTask,
        taskDone: nextTaskDone,
        lastEditedBy: currentUserId,
      });
      closeContextMenu();
      return;
    }

    // Guest / legacy path: use local helper.
    toggleTaskFlag(nodeId);
    saveIfChanged();
    closeContextMenu();
  }, [
    contextMenu.nodeId,
    isLoggedIn,
    yActions,
    getNode,
    toggleTaskFlag,
    saveCurrentState,
    saveIfChanged,
    closeContextMenu,
    currentUserId,
  ]);

  // Wrap map navigation mouse-up to persist the new center position
  // after a pan gesture so the map center is saved and undoable.
  const handleMouseUp = useCallback((e) => {
    navHandleMouseUp(e);
    // Only save when a pan may have occurred (nodes/root moved)
    saveCurrentState();
    saveIfChanged();
  }, [navHandleMouseUp, saveCurrentState, saveIfChanged]);

  const cancelNodeTouchGestures = useCallback(() => {
    Object.values(nodeRefs.current).forEach((nodeRef) => {
      nodeRef?.cancelTouchGesture?.();
    });
  }, []);

  const handlePointerDownCapture = useCallback((event) => {
    if (isSearchOpen) {
      closeMapSearch({ blur: true });
    }
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      return;
    }
    const nodeElement = event?.target?.closest?.('[data-node-id]');
    if (nodeElement && event.button === 0 && event.isPrimary !== false) {
      recentNodePointerDownRef.current = {
        at: Date.now(),
        x: event.clientX,
        y: event.clientY,
      };
    }
    const result = navHandlePointerDown(event);
    if (result?.startedMultiTouch) {
      cancelNodeTouchGestures();
    }
  }, [
    cancelNodeTouchGestures,
    closeMapSearch,
    isSearchOpen,
    navHandlePointerDown,
  ]);

  const handleDoubleClickCapture = useCallback((event) => {
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event?.target?.closest?.('[data-node-id]')) {
      recentNodePointerDownRef.current = null;
    }
  }, []);

  const handlePointerUpCapture = useCallback((event) => {
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      return;
    }
    navHandlePointerUp(event);
  }, [navHandlePointerUp]);

  const handlePointerCancelCapture = useCallback((event) => {
    navHandlePointerCancel(event);
  }, [navHandlePointerCancel]);

  const handlePointerUp = useCallback(() => {
    saveCurrentState();
    saveIfChanged();
  }, [saveCurrentState, saveIfChanged]);

  const handlePointerCancel = useCallback(() => {
    saveCurrentState();
    saveIfChanged();
  }, [saveCurrentState, saveIfChanged]);

  // Automatically enrich nodes whose text is a bare URL with metadata,
  // so the preview is ready the first time the user clicks on them.
  const pendingUrlMetaRef = useRef(new Set());
  const attemptedMindmapThumbnailRef = useRef(new Set());
  const mindmapPreviewRefreshRef = useRef(new Map());

  useEffect(() => {
    const urlOnlyRegex = /^https?:\/\/[^\s]+$/i;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    const getEmbeddedMapId = (candidate) => {
      if (!candidate) return null;
      try {
        const urlObj = new URL(candidate);
        const mapId = urlObj.searchParams.get('map');
        if (!mapId || !uuidRegex.test(mapId)) {
          return null;
        }

        const host = urlObj.hostname.toLowerCase();
        const normalizedHost = host.replace(/^www\./, '');
        const currentHost = (typeof window !== 'undefined' && window.location && window.location.hostname)
          ? window.location.hostname.toLowerCase()
          : null;
        const isLocalHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1';
        const matchesCurrentHost = currentHost ? normalizedHost === currentHost : false;
        const isMindmappiHost = normalizedHost.includes('mindmappi');

        if (isLocalHost || matchesCurrentHost || isMindmappiHost) {
          return mapId;
        }

        return null;
      } catch {
        return null;
      }
    };

    nodes.forEach((node) => {
      const rawText = typeof node.text === 'string' ? node.text.trim() : '';
      const linkUrlCandidate =
        typeof node.linkUrl === 'string' && node.linkUrl.trim().length
          ? node.linkUrl.trim()
          : '';

      let urlToUse = null;
      if (linkUrlCandidate) {
        urlToUse = linkUrlCandidate;
      } else if (rawText) {
        if (urlOnlyRegex.test(rawText)) {
          urlToUse = rawText;
        }
      }

      if (!urlToUse) {
        return;
      }

      const mindmapId = getEmbeddedMapId(urlToUse);
      const missingLinkUrl = !linkUrlCandidate;
      const missingLinkMeta = !node.linkMeta;
      const missingMindmapThumbnail = mindmapId && (!node.linkMeta || !node.linkMeta.image);

      const shouldFetchBasicMeta = missingLinkUrl || missingLinkMeta;
      const shouldRefreshMindmapThumbnail = missingMindmapThumbnail;

      if (!shouldFetchBasicMeta && !shouldRefreshMindmapThumbnail) {
        return;
      }

      if (pendingUrlMetaRef.current.has(node.id)) {
        return;
      }

      if (shouldRefreshMindmapThumbnail && attemptedMindmapThumbnailRef.current.has(node.id)) {
        return;
      }

      pendingUrlMetaRef.current.add(node.id);

      (async () => {
        try {
          const meta = await fetchUrlPreview(urlToUse);
          const cleanedTitle = meta && meta.title
            ? decodeHtmlEntities(meta.title).trim()
            : null;
          const enrichedMeta = {
            ...meta,
            title: cleanedTitle || meta.title,
          };

          if (shouldRefreshMindmapThumbnail && mindmapId) {
            attemptedMindmapThumbnailRef.current.add(node.id);
          }

          if (isLoggedIn && yActions && yActions.updateNode) {
            // Logged-in (Yjs) path: write link metadata through Yjs
            // so the document remains the source of truth.
            const currentText = rawText;
            const patch = {
              linkUrl: urlToUse,
              linkMeta: enrichedMeta,
              lastEditedBy: currentUserId,
            };
            if (urlOnlyRegex.test(currentText) && enrichedMeta.title && currentText === urlToUse) {
              patch.text = enrichedMeta.title;
            }
            yActions.updateNode(node.id, patch);
          } else {
            // Guest / legacy path: mutate local React state and persist
            setNodes((prev) => prev.map((n) => {
              if (n.id !== node.id) return n;
              const currentText = (n.text || '').trim();

              const shouldOverrideText =
                urlOnlyRegex.test(currentText) && enrichedMeta.title && currentText === urlToUse;

              if (shouldOverrideText) {
                return {
                  ...n,
                  linkUrl: urlToUse,
                  linkMeta: enrichedMeta,
                  text: enrichedMeta.title,
                };
              }

              return {
                ...n,
                linkUrl: urlToUse,
                linkMeta: enrichedMeta,
              };
            }));
            saveIfChanged();
          }
        } catch (err) {
          console.error('Failed to auto-fetch URL metadata', err);
        } finally {
          pendingUrlMetaRef.current.delete(node.id);
        }
      })();
    });
  }, [nodes, setNodes, saveIfChanged, isLoggedIn, yActions, currentUserId]);

  // Periodically refresh metadata (title/thumbnail) for nodes that link
  // to other Mindmappi maps so their labels stay in sync even if those
  // maps' root titles change in the background.
  useEffect(() => {
    const intervalMs = 15 * 1000; // 30 seconds
    const urlOnlyRegex = /^https?:\/\/[^\s]+$/i;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    const getEmbeddedMapId = (candidate) => {
      if (!candidate) return null;
      try {
        const urlObj = new URL(candidate);
        const mapId = urlObj.searchParams.get('map');
        if (!mapId || !uuidRegex.test(mapId)) {
          return null;
        }

        const host = urlObj.hostname.toLowerCase();
        const normalizedHost = host.replace(/^www\./, '');
        const currentHost = (typeof window !== 'undefined' && window.location && window.location.hostname)
          ? window.location.hostname.toLowerCase()
          : null;
        const isLocalHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1';
        const matchesCurrentHost = currentHost ? normalizedHost === currentHost : false;
        const isMindmappiHost = normalizedHost.includes('mindmappi');

        if (isLocalHost || matchesCurrentHost || isMindmappiHost) {
          return mapId;
        }

        return null;
      } catch {
        return null;
      }
    };

    const tick = () => {
      const now = Date.now();

      nodes.forEach((node) => {
        const rawText = typeof node.text === 'string' ? node.text.trim() : '';
        const linkUrlCandidate =
          typeof node.linkUrl === 'string' && node.linkUrl.trim().length
            ? node.linkUrl.trim()
            : '';

        let urlToUse = null;
        if (linkUrlCandidate) {
          urlToUse = linkUrlCandidate;
        } else if (rawText) {
          if (urlOnlyRegex.test(rawText)) {
            urlToUse = rawText;
          }
        }

        if (!urlToUse) return;

        const mindmapId = getEmbeddedMapId(urlToUse);
        if (!mindmapId) return;

        // Avoid overlapping with the one-off enrichment flow.
        if (pendingUrlMetaRef.current.has(node.id)) return;

        const last = mindmapPreviewRefreshRef.current.get(mindmapId) || 0;
        if (now - last < intervalMs) return;

        mindmapPreviewRefreshRef.current.set(mindmapId, now);
        pendingUrlMetaRef.current.add(node.id);

        (async () => {
          try {
            const meta = await fetchUrlPreview(urlToUse);
            const cleanedTitle = meta && meta.title
              ? decodeHtmlEntities(meta.title).trim()
              : null;
            const enrichedMeta = {
              ...meta,
              title: cleanedTitle || meta.title,
            };

            if (isLoggedIn && yActions && yActions.updateNode) {
              // Yjs-backed: merge refreshed metadata and, for map links
              // whose label hasn't been manually customised, keep the
              // node text in sync with the map title.
              const newTitle = enrichedMeta && enrichedMeta.title
                ? enrichedMeta.title.trim()
                : null;
              const patch = buildLinkedMapTitlePatch(node, {
                mapId: mindmapId,
                title: newTitle || node.linkMeta?.title || rawText,
                metadata: enrichedMeta,
                currentUserId,
              });
              if (!patch) return;
              patch.linkUrl = urlToUse;
              yActions.updateNode(node.id, patch);
            } else {
              // Guest / legacy path: merge refreshed metadata into local state.
              setNodes((prev) => prev.map((n) => {
                if (n.id !== node.id) return n;
                const newTitle = enrichedMeta && enrichedMeta.title
                  ? enrichedMeta.title.trim()
                  : '';
                const patch = buildLinkedMapTitlePatch(n, {
                  mapId: mindmapId,
                  title: newTitle || n.linkMeta?.title || n.text || '',
                  metadata: enrichedMeta,
                  currentUserId,
                });
                if (!patch) return n;

                return {
                  ...n,
                  ...patch,
                  linkUrl: urlToUse,
                };
              }));
              saveIfChanged();
            }
          } catch (err) {
            console.error('Failed to refresh map-link URL metadata', err);
          } finally {
            pendingUrlMetaRef.current.delete(node.id);
          }
        })();
      });
    };

    tick();
    const id = window.setInterval(tick, intervalMs);
    return () => {
      window.clearInterval(id);
    };
  }, [nodes, isLoggedIn, yActions, currentUserId, setNodes, saveIfChanged]);

  const handleContainerClick = (event) => {
    if (isSearchOpen) {
      closeMapSearch({ blur: true });
    }
    // Toolbar, menu, picker, and navigation controls live inside the canvas
    // DOM tree but are not canvas background. Their clicks must not schedule
    // a delayed selection clear after the control action has completed.
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      return;
    }
    // Mobile browsers may emit a compatibility click immediately after the
    // touch release that completed a long press. Do not let that synthetic
    // background click dismiss the menu that gesture just opened.
    if (Date.now() - contextMenuOpenedAtRef.current < 750) {
      return;
    }
      /*
      console.log('click')
      if (showPicker) {
        setShowPicker(false);
      } else {
        if(selectedId)lastSelectedIdRef.current = selectedId; // Store last selected ID
        setSelectedId(null);
      }
        */
      console.log('click')

    // Hide context menu on background click
    if (contextMenu.visible) {
      setContextMenu(prev => ({ ...prev, visible: false }));
    }

    // Hide emoji picker on background click
    if (showEmojiPicker) {
      setShowEmojiPicker(false);
    }

    // Clear any existing timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      return; // This was a double-click, don't process single click
    }

    // Set a timeout for single click processing
    const timeout = setTimeout(() => {
      if (showPicker) {
        setShowPicker(false);
        setColorPosOverride(null);
      } else {
        if(selectedId) lastSelectedIdRef.current = selectedId;
        setSelectedId(null);
      }
      setClickTimeout(null);
    }, 300); // 300ms delay to detect double-clicks

    setClickTimeout(timeout);
  };

  const handleEmojiSelect = useCallback((emoji) => {
    if (!selectedId) {
      setShowEmojiPicker(false);
      return;
    }

    const node = getNode(selectedId);
    const isForeignOwner = !!(node && node.userId) && !!currentUserId && node.userId !== currentUserId;
    if (isForeignOwner) {
      setShowEmojiPicker(false);
      showNotification('You can only change the emoji of nodes you created.', 'error');
      return;
    }

    saveCurrentState();

    // Logged-in (Yjs) path: store emoji in Yjs so it syncs.
    if (isLoggedIn && yActions && yActions.updateNode) {
      yActions.updateNode(selectedId, { emoji, lastEditedBy: currentUserId });
    } else {
      // Guest / legacy path: local React state.
      setNodes(prev => prev.map(n => (
        n.id === selectedId ? { ...n, emoji } : n
      )));
      // Persist immediately so emoji changes are not lost if
      // there are no further actions before closing the tab.
      persistNow();
    }

    setShowEmojiPicker(false);
  }, [selectedId, isLoggedIn, yActions, saveCurrentState, setNodes, persistNow, getNode, currentUserId, showNotification]);

  const handleDoubleClick = (e) => {
    if (e?.target?.closest?.('[data-export-ignore="true"]')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Prevent default double-click behavior
    e.preventDefault();
    e.stopPropagation();

    // A node can move between the two presses because of collaboration or
    // collision layout. In that case the browser targets the resulting
    // double-click at the canvas; keep the gesture owned by the node instead
    // of interpreting it as background child creation.
    const recentNodePointerDown = recentNodePointerDownRef.current;
    if (recentNodePointerDown) {
      const elapsed = Date.now() - recentNodePointerDown.at;
      const distance = Math.hypot(
        e.clientX - recentNodePointerDown.x,
        e.clientY - recentNodePointerDown.y
      );
      recentNodePointerDownRef.current = null;
      if (
        elapsed >= 0
        && elapsed <= NODE_DOUBLE_CLICK_GUARD_MS
        && distance <= NODE_DOUBLE_CLICK_GUARD_DISTANCE
      ) {
        return;
      }
    }

    const selectedNode = getSelectedNode();
    const base = selectedNode
      || (lastSelectedIdRef.current && getNode(lastSelectedIdRef.current))
      || (root && getNode(root))
      || nodes.find((node) => node.isRoot);
    if (!base) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const worldPoint = screenToWorld(
      { x: e.clientX, y: e.clientY },
      containerRect,
      { scale, offset }
    );

    handleAddChildNode(base.id, worldPoint);
  };


  const colorPosFallback = selectedNodePosition();
  const colorPos = colorPosOverride || colorPosFallback;
  const reparentViewportPosition = worldToViewport(
    reparentCandidate.position,
    { scale, offset }
  );
  const contextMenuViewportSize = {
    width: containerRef.current?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0),
    height: containerRef.current?.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 0),
  };
  const contextMenuPosition = clampViewportOverlay(
    contextMenu,
    contextMenuViewportSize,
    {
      width: 220,
      height: Math.min(560, Math.max(0, contextMenuViewportSize.height - 16)),
    },
    8
  );
  const activeDiscussionNode = nodeDiscussions.activeNodeId
    ? nodes.find((node) => node.id === nodeDiscussions.activeNodeId)
    : null;
  const activeDiscussionNodeRect = activeDiscussionNode
    ? nodeRefs.current[activeDiscussionNode.id]?.getBoundingClientRect?.()
    : null;
  const discussionAnchorRect = activeDiscussionNodeRect
    ? {
        left: activeDiscussionNodeRect.left,
        right: activeDiscussionNodeRect.right,
        top: activeDiscussionNodeRect.top,
        bottom: activeDiscussionNodeRect.bottom,
        width: activeDiscussionNodeRect.width,
        height: activeDiscussionNodeRect.height,
      }
    : null;
  const buttonStyle = {
    background: 'transparent',
    border: 'none',
    padding: 2,
    margin: 2,
    cursor: 'pointer',
    outline: 'none',
    color: getThemeCanvasForeground(mapTheme, isDarkMode),
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const handleShare = useCallback(() => {
    setIsShareOpen(true);
  }, []);

  const edgeRoutingNodes = nodes
    .filter((node) => visibleNodeIds.has(node.id))
    .map((node) => {
      const position = getRenderedNodePosition(node, dragPositionPreview);
      const measuredSize = readNodeLayoutSize(nodeRefs.current[node.id], scale);
      return {
        id: node.id,
        rect: {
          x: position.x,
          y: position.y,
          width: measuredSize?.width || node.layoutSize?.width || NODE_WIDTH_GUESS,
          height: measuredSize?.height || node.layoutSize?.height || NODE_HEIGHT_GUESS,
        },
      };
    });
  const edgeRoutingRelationships = relationships.filter(
    ({ parentId, childId }) => (
      visibleNodeIds.has(parentId) && visibleNodeIds.has(childId)
    )
  );
  const isSettledEdgeRoutingEnabled = (
    !draggedNodeId && layoutTransitionNodeIds.size === 0
  );
  const edgeRoutingGeometryKey = JSON.stringify({
    nodes: edgeRoutingNodes.map(({ id, rect }) => [id, rect.x, rect.y, rect.width, rect.height]),
    relationships: edgeRoutingRelationships.map(
      ({ parentId, childId }) => [parentId, childId]
    ),
    avoidObstacles: isSettledEdgeRoutingEnabled,
  });
  const routedVisibleEdges = useMemo(() => {
    return routeEdges({
      nodes: edgeRoutingNodes,
      edges: edgeRoutingRelationships,
      // Package 1 settles all visible routes after a gesture. Package 2 will
      // reroute only affected edges with hysteresis inside the drag budget.
      // Keep the attached boundary curve during the collision slide too, so
      // nodes and paths animate as one shape before the settled route changes.
      avoidObstacles: isSettledEdgeRoutingEnabled,
    });
    // The serialized key contains every geometry field consumed above. Node
    // text, selection, and other presentation changes must not rerun routing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    edgeRoutingGeometryKey,
  ]);

  return (
    <div
      data-layout-version={layoutVersion}
      style={{
        background: getThemeCanvasBackground(mapTheme, isDarkMode),
        width: '100%',
        position: 'absolute',
        height: '100%',
        left: 0,
        top: 0,
        overflow: 'clip',
        right: 0,
        bottom: 0,
        opacity: isInitialized ? 1 : 0,
        transition: 'opacity 150ms ease-out',

      }}
    >
      {/* Loading overlay shown until the mind map is initialized */}
      {!isInitialized && (
        <div className="mind-map-loading">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="mind-map-loading__spinner" />
            <div className="mind-map-loading__text">Loading your mind map</div>
          </div>
        </div>
      )}

      {/* Main container */}
      <div
        ref={containerRef}
        tabIndex={0}
        role="tree"
        aria-label="Mind map canvas"
        data-testid="mind-map-canvas"
        data-map-access-role={accessRole || (isLoggedIn ? 'pending' : 'guest')}
        data-collaboration-status={
          !isLoggedIn
            ? 'guest'
            : (
              yIsNetworkSynced && yConnectionStatus === 'connected'
                ? 'synced'
                : yConnectionStatus
            )
        }
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onPointerDownCapture={handlePointerDownCapture}
        onDoubleClickCapture={handleDoubleClickCapture}
        onPointerMove={navHandlePointerMove}
        onPointerUpCapture={handlePointerUpCapture}
        onPointerUp={handlePointerUp}
        onPointerCancelCapture={handlePointerCancelCapture}
        onPointerCancel={handlePointerCancel}
        onClick={handleContainerClick}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        style={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          overflow: 'clip',
          outline: 'none',
          touchAction: 'none'
        }}
      >
        <FocusNavigation
          path={focusPath}
          isDarkMode={isDarkMode}
          canGoBack={focusHistory.index > 0}
          canGoForward={focusHistory.index < focusHistory.entries.length - 1}
          onBack={() => navigateFocusHistory(-1)}
          onForward={() => navigateFocusHistory(1)}
          onNavigate={(nodeId) => updateFocusView(nodeId)}
        />

        {/* Scaled content container (export target: nodes + lines only) */}
        <div
          style={{
              transform: `translate(${offset.x}px, ${offset.y}px)`,
              width: `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              top: 0,
              left: 0,
              position: 'relative',
          }}
        >
            <div
              ref={captureRef}
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                width: `${100 / scale}%`,
                height: `${100 / scale}%`,
                position: 'relative',
              }}
            >
          {/* SVG for connections */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 0,
              overflow: 'visible'
            }}
            overflow="visible"
          >
            <g>
              {relationships.map(({ parentId, childId }) => {
                const parentNode = nodes.find(n => n.id === parentId);
                const childNode = nodes.find(n => n.id === childId);

                if (!parentNode || !childNode) return null;

                const parentVisible = visibleNodeIds.has(parentId);
                const childVisible = visibleNodeIds.has(childId);

                if (!parentVisible || !childVisible) {
                  return null;
                }

                const resolvedRoute = routedVisibleEdges.get(`${parentId}::${childId}`);
                const parentRect = resolvedRoute?.sourceRect;
                const childRect = resolvedRoute?.targetRect;

                return (
                  <Line
                    key={`${parentId}-${childId}`}
                    fromRect={parentRect}
                    toRect={childRect}
                    resolvedRoute={resolvedRoute}
                    opacity={1}
                    theme={mapTheme}
                    isDarkMode={isDarkMode}
                    isSelected={selectedId === parentId || selectedId === childId}
                    isMuted={Boolean(selectedId && selectedId !== parentId && selectedId !== childId)}
                    fromId={parentId}
                    toId={childId}
                    isLayoutAnimating={
                      layoutTransitionNodeIds.has(parentId)
                      || layoutTransitionNodeIds.has(childId)
                    }
                  />
                );
              })}
            </g>
          </svg>

          {/* Nodes */}
          {nodes.map(node => {
            const isVisible = visibleNodeIds.has(node.id);
            const isHighlighted = highlightedNodeId === node.id;
            const safePosition = getRenderedNodePosition(node, dragPositionPreview);

            // Check if this node has visible children
            const hasVisibleChildren = relationships.some(rel =>
              rel.parentId === node.id &&
              visibleNodeIds.has(rel.childId) &&
              nodes.some(childNode => childNode.id === rel.childId)
            );

            // Get the current children's positions for smart button placement
            const currentChildren = relationships
              .filter(rel => rel.parentId === node.id)
              .map(rel => nodes.find(child => child.id === rel.childId))
              .filter((child) => child && visibleNodeIds.has(child.id));

            let collapsedChildren = null;
            if (collapsedNodeIds.has(node.id)) {
              const branchIds = getBranchNodeIds(node.id, relationships);
              const hiddenNodes = nodes.filter((candidate) => (
                candidate.id !== node.id
                && branchIds.has(candidate.id)
                && !visibleNodeIds.has(candidate.id)
              ));
              collapsedChildren = { nodes: hiddenNodes, count: hiddenNodes.length };
            }

            if (!isVisible) {
              return null;
            }

            return (
              <Node
                key={node.id}
                id={node.id}
                ref={el => nodeRefs.current[node.id] = el}
                initialText={node.text}
                initialX={safePosition.x}
                initialY={safePosition.y}
                userId={node.userId}
                creatorName={node.creatorName}
                currentUserId={currentUserId}
                onTextChange={text => {
                  if (isLoggedIn && yActions && yActions.updateNode) {
                    yActions.updateNode(node.id, { text, lastEditedBy: currentUserId });
                  } else {
                    updateNodeText(node.id, text);
                  }
                }}
                onPositionChange={pos => handleNodePositionChange(node.id, pos)}
                screenToCanvasPosition={screenPointToCanvas}
                focusNodeIfOutOfView={focusNodeIfOutOfView}
                isSelected={selectedId === node.id}
                onSelect={handleNodeSelect}
                x={safePosition.x}
                y={safePosition.y}
                animate={node.animate}
                opacity={1}
                onKeyDown={handleKeyDown}
                color={node.color}
                theme={mapTheme}
                isRoot={node.isRoot}
                isTask={!!node.isTask}
                taskDone={!!node.taskDone}
                linkUrl={node.linkUrl}
                linkMeta={node.linkMeta}
                emoji={node.emoji}
                embeddedMindmapId={node.embeddedMindmapId}
                isEmbeddedMindmapLink={!!node.isEmbeddedMindmapLink}
                canEdit={
                  canWriteMap
                  && !(node.userId && currentUserId && node.userId !== currentUserId)
                }
                discussionSummary={nodeDiscussions.summaries[node.id] || null}
                onOpenDiscussion={
                  canUseNodeDiscussions ? openNodeDiscussion : undefined
                }
                onDisallowedEdit={() => {
                  showNotification(
                    canWriteMap
                      ? 'You can only edit nodes you created.'
                      : 'This map is view only.',
                    'error'
                  );
                }}
                lockPositionToState={
                  (node.id === root && !focusRootId)
                  || dragPositionPreview.has(node.id)
                }
                isPositionAnimating={layoutTransitionNodeIds.has(node.id)}
                onMapLinkTitleCommit={node.embeddedMindmapId ? (newTitle) => handleMapLinkTitleCommit(node.embeddedMindmapId, newTitle) : undefined}
                onDragComplete={handleNodeDragComplete}
                editComplete={handleNodeEditComplete}
                onDragStart={(pointer) => handleNodeDragStart(node.id, pointer)}
                onDragMove={(pos, pointer) => handleNodeDragMove(node.id, pos, pointer)}
                onDragEnd={handleNodeDragEnd}
                isHighlighted={isHighlighted}
                collapsedChildren={collapsedChildren}
                hasChildren={hasVisibleChildren}
                currentChildren={currentChildren}
                onEditStart={saveCurrentState}
                onExpandNode={(nodeId) => {
                  expandBranch(nodeId);
                }}
                onCollapseNode={(nodeId) => {
                  collapseBranch(nodeId);
                }}
                getChildrenPositions={getChildrenPositions}
                getParentNodeData={getParentNodeData}
                onSizeChange={handleNodeSizeChange}
                onToggleTaskDone={() => {
                  const isForeignOwner = !!node.userId && !!currentUserId && node.userId !== currentUserId;
                  if (isForeignOwner) {
                    showNotification('You can only edit tasks you created.', 'error');
                    return;
                  }

                  saveCurrentState();
                  if (isLoggedIn && yActions && yActions.updateNode) {
                    const currentlyDone = !!node.taskDone;
                    yActions.updateNode(node.id, {
                      isTask: true,
                      taskDone: !currentlyDone,
                      lastEditedBy: currentUserId,
                    });
                  } else {
                    toggleTaskDone(node.id);
                    saveIfChanged();
                  }
                }}
                onClearLinkMeta={() => {
                  saveCurrentState();
                  setNodes(prev => prev.map(n => {
                    if (n.id !== node.id) return n;
                    const updated = { ...n };
                    delete updated.linkUrl;
                    delete updated.linkMeta;
                    return updated;
                  }));
                  saveIfChanged();
                }}
                onOpenPreview={async (nodeId, url) => {
                  try {
                    const meta = await fetchUrlPreview(url);
                    const cleanedTitle = meta && meta.title
                      ? decodeHtmlEntities(meta.title).trim()
                      : null;
                    const enrichedMeta = {
                      ...meta,
                      title: cleanedTitle || meta.title,
                    };
                    // Persist URL + meta; only overwrite text with
                    // the fetched title if the current text is still
                    // the raw URL (user hasn't customised it yet).
                    if (isLoggedIn && yActions && yActions.updateNode) {
                      const currentText = (node.text || '').trim();
                      const patch = {
                        linkUrl: url,
                        linkMeta: enrichedMeta,
                        lastEditedBy: currentUserId,
                      };
                      if (enrichedMeta.title && currentText === url) {
                        patch.text = enrichedMeta.title;
                      }
                      yActions.updateNode(nodeId, patch);
                    } else {
                      setNodes(prev => prev.map(n => {
                        if (n.id !== nodeId) return n;
                        const currentText = (n.text || '').trim();
                        if (enrichedMeta.title && currentText === url) {
                          return {
                            ...n,
                            linkUrl: url,
                            linkMeta: enrichedMeta,
                            text: enrichedMeta.title,
                          };
                        }
                        return {
                          ...n,
                          linkUrl: url,
                          linkMeta: enrichedMeta,
                        };
                      }));
                      saveIfChanged();
                    }
                  } catch (err) {
                    console.error('Failed to fetch URL metadata', err);
                  }
                }}
                onContextMenu={handleNodeContextMenu}
              />
            );
          })}
          </div>
        </div>

        <ReparentDropIndicator
          isVisible={reparentCandidate.visible}
          draggedNode={reparentCandidate.draggedNode}
          targetNode={reparentCandidate.targetNode}
          position={reparentViewportPosition}
        />

        {/* Color picker overlay */}
        {showPicker && (
          <div
            style={{
              position: 'absolute',
              left: `${colorPos.x}px`,
              top: `${colorPos.y}px`,
              zIndex: 100
            }}
            data-export-ignore="true"
          >
            <ColorPickerWidget
              x={0}
              y={0}
              colors={mapTheme.palette}
              onColorSelect={handleColorSelect}
            />
          </div>
        )}

        {/* Emoji picker overlay */}
        {showEmojiPicker && (
          <div
            style={{
              position: 'absolute',
              left: `${emojiPos.x}px`,
              top: `${emojiPos.y}px`,
              zIndex: 110,
            }}
            data-export-ignore="true"
          >
            <EmojiPickerWidget
              x={0}
              y={0}
              onEmojiSelect={handleEmojiSelect}
            />
          </div>
        )}

        {/* Node context menu */}
        {contextMenu.visible && (
          <div
            data-testid="node-context-menu"
            style={{
              position: 'absolute',
              left: `${contextMenuPosition.x}px`,
              top: `${contextMenuPosition.y}px`,
              zIndex: 120,
              maxHeight: `${Math.max(80, contextMenuViewportSize.height - 16)}px`,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
            data-export-ignore="true"
          >
            {(() => {
              const node = nodes.find(n => n.id === contextMenu.nodeId);
              const isTask = !!(node && node.isTask);
              return (
                <NodeContextMenu
                  isDarkMode={isDarkMode}
                  onAddChild={handleContextAddChild}
                  onAddSibling={handleContextAddSibling}
                  onAddTaskChild={handleContextAddTaskChild}
                  onDelete={handleContextDeleteNode}
                  onChangeColor={handleContextChangeColor}
                  onChangeEmoji={handleContextChangeEmoji}
                  onFocusBranch={handleContextFocusBranch}
                  onOpenBranch={handleContextOpenBranch}
                  onCopyLink={handleContextCopyLink}
                  isTask={isTask}
                  onToggleTask={handleContextToggleTask}
                  onCreateMindmapLink={handleContextCreateMindmapLink}
                  canCreateMindmapLink={!!currentUserId}
                  canAddSibling={!!node && !node.isRoot}
                  canWrite={canWriteMap}
                  canDiscuss={canUseNodeDiscussions}
                  onOpenDiscussion={handleContextOpenDiscussion}
                />
              );
            })()}
          </div>
        )}

        {canUseNodeDiscussions && activeDiscussionNode && (
          <NodeDiscussionPanel
            node={activeDiscussionNode}
            anchorRect={discussionAnchorRect}
            isDarkMode={isDarkMode}
            theme={mapTheme}
            currentUserId={currentUserId}
            thread={nodeDiscussions.thread}
            isLoading={nodeDiscussions.isLoadingThread}
            isLoadingOlder={nodeDiscussions.isLoadingOlder}
            isSending={nodeDiscussions.isSending}
            error={nodeDiscussions.error}
            onClose={nodeDiscussions.closeDiscussion}
            onLoadOlder={nodeDiscussions.loadOlder}
            onSend={nodeDiscussions.sendMessage}
            onRefresh={nodeDiscussions.refreshActiveDiscussion}
          />
        )}

        <div
          className="left-menu-scroll"
          aria-hidden={isDistractionFree && !areFullscreenMenusVisible}
          style={{
            marginBottom: '10px',
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            position: 'fixed',
            top: 60,
            left: 10,
            zIndex: 10,
            maxHeight: 'calc(100vh - 80px)',
            overflowY: 'auto',
            opacity: !isDistractionFree || areFullscreenMenusVisible ? 1 : 0,
            transform: !isDistractionFree || areFullscreenMenusVisible
              ? 'translateX(0)'
              : 'translateX(-14px)',
            pointerEvents: !isDistractionFree || areFullscreenMenusVisible ? 'auto' : 'none',
            visibility: !isDistractionFree || areFullscreenMenusVisible ? 'visible' : 'hidden',
            transition: !isDistractionFree || areFullscreenMenusVisible
              ? 'opacity 220ms ease-out, transform 220ms ease-out, visibility 0s linear'
              : 'opacity 180ms ease-in, transform 180ms ease-in, visibility 0s linear 180ms',
          }}
          data-export-ignore="true"
        >
              <Menu handleAddChildNode={handleAddChildNode}
                saveMindMap={saveMindMap}
                loadMindMap={loadMindMap}
                handleRemoveNode={handleRemoveNode}
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                setShowPicker={setShowPicker}
                showPicker={showPicker}
                isDarkMode={isDarkMode}
                canUndo={canUndo}
                canRedo={canRedo}
                showHome={Boolean(focusRootId && focusRootId !== root)}
                onHome={handleHome}
                onExport={handleExportImage}
                onExportData={isLoggedIn ? handleExportData : undefined}
                onSaveThumbnail={isLoggedIn && canManageMapMetadata && mindmapIdForYjs ? handleSaveThumbnail : undefined}
                onShare={handleShare}
                canWrite={canWriteMap}
              />
      </div>

      {isLoggedIn && accessRole === 'viewer' && (
        <div
          className="mind-map-read-only"
          role="status"
          aria-label="View-only map"
          data-export-ignore="true"
          style={{
            position: 'absolute',
            top: 14,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 130,
            padding: '7px 12px',
            borderRadius: 999,
            background: isDarkMode ? 'rgba(31, 41, 55, 0.92)' : 'rgba(255, 255, 255, 0.94)',
            color: isDarkMode ? '#f9fafb' : '#111827',
            border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
            fontSize: 13,
            fontWeight: 600,
            pointerEvents: 'none',
          }}
        >
          View only
        </div>
      )}

      <ShareDialog
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        mindmapId={mindmapIdForYjs}
        canManage={canManageMapMetadata}
        isDarkMode={isDarkMode}
        showNotification={showNotification}
      />

      {isLoggedIn
        && yIsLocallySynced
        && (!yIsNetworkSynced || yConnectionStatus !== 'connected')
        && (
          <div
            className="mind-map-offline-status"
            role="status"
            aria-label="Offline map"
            data-export-ignore="true"
          >
            {remoteMapData?.offlineCache
              ? 'Recovered from this device · waiting to sync'
              : 'Offline · changes are saved on this device'}
          </div>
        )}

      {notifications.length > 0 && (
        <div className="mind-map-notifications" data-export-ignore="true">
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`mind-map-notification mind-map-notification--${n.variant}`}
            >
              {n.message}
            </div>
          ))}
        </div>
      )}

        {/* Zoom controls - moved to bottom-right to avoid overlapping the top menu */}
        <div
          className="zoom-controls"
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            left: 'auto',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            maxWidth: 'calc(100vw - 40px)'
          }}
          data-export-ignore="true"
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {isDistractionFree && (
            <button
              onClick={() => setAreFullscreenMenusVisible((visible) => !visible)}
              style={{ ...buttonStyle, width: 30, height: 30 }}
              title={areFullscreenMenusVisible ? 'Hide menus' : 'Show menus'}
              aria-label={areFullscreenMenusVisible ? 'Hide menus' : 'Show menus'}
              aria-pressed={areFullscreenMenusVisible}
            >
              <svg
                aria-hidden="true"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          )}
          <button
            onClick={isDistractionFree ? exitDistractionFree : enterDistractionFree}
            style={{ ...buttonStyle, width: 30, height: 30 }}
            title={isDistractionFree ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-label={isDistractionFree ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isDistractionFree
                ? <path d="M3 8h5V3M21 8h-5V3M16 21v-5h5M8 21v-5H3" />
                : <path d="M8 3H3v5M16 3h5v5M21 16v5h-5M8 21H3v-5" />}
            </svg>
          </button>
          <button
            onClick={handleFitVisible}
            style={buttonStyle}
            title="Fit visible map (Shift+1)"
            aria-label="Fit visible map"
          >
            ◎
          </button>
          <button
            onClick={zoomOut}
            style={{ ...buttonStyle, width: 30, height: 30, lineHeight: 1 }}
            aria-label="Zoom out"
          >
            −
          </button>
          <div
            data-testid="zoom-level"
            style={{
              color: getThemeCanvasForeground(mapTheme, isDarkMode),
              fontSize: 14,
              minWidth: 44,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              margin: 2,
              cursor: 'pointer'
            }}
            onClick={resetZoom}
          >
            {parseInt(scale * 100)}%
          </div>
          <button
            onClick={zoomIn}
            style={{ ...buttonStyle, width: 30, height: 30, lineHeight: 1 }}
            aria-label="Zoom in"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodesContainer;
